import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { injectInfiniteQuery, injectMutation, injectQuery, injectQueryClient } from './query'
import { Query, QueryObserverResult } from '@tanstack/query-core'
import { catchError, firstValueFrom, forkJoin, map, mergeMap, Observable, of, tap } from 'rxjs'
import { format as formatDate } from 'date-fns'
import type { CitationMinuteNodes } from '../../../backend/src/citations/minutes-citations-api.dto'
import { DemoProfilesService } from './demo-profiles.service'
import { DownloadsService } from './downloads.service'
import { MeetingFilters } from './filters/filters.component'
import { GuestOrganization } from './guest-organizations.service'
import { ListParameterQueryOptional } from './list-query-options'
import { LoggerService } from './logger.service'
import { MetricsService } from './metrics.service'
import { Team } from './teams.service'
import { queryKeys } from './query-keys'
import { MINIMUM_SEGMENT_DURATION } from './speaker-x-ray/speaker-x-ray.component'
import { ToastService } from './toast.service'
import { User } from './users.service'
import { MinuteTimelineSegment, TemplateContentBlockSourceType, TemplateContentBlockType } from './template-editor.component/types/template.types'
import { DocumentUploadInfo, generateFileName, generateHeadersWithTimezone, qualifyFileName } from './utils'
import { DateRange } from './common/date-range-picker/date-range-picker.component'
import { MeetingProcessingStatus, MeetingSessionStatus, MeetingStatus, MeetingType } from './models/meeting-enums'
import type { BatchJobType, MeetingProvider, ConsentResponse } from './models/meeting-enums'

export { MeetingProcessingStatus, MeetingSessionStatus, MeetingStatus, MeetingType } from './models/meeting-enums'
export type { BatchJobType, MeetingProvider, ConsentResponse } from './models/meeting-enums'

const ONGOING_MEETINGS_FAST_POLL_DURATION_MS = 15_000

export function anyFiltersApplied(filters: MeetingFilters, includeQuery = true): boolean {
  const isQueryActive = includeQuery ? filters.query !== null && filters.query !== '' : false
  return (
    isQueryActive ||
    filters.guestOrganizationId !== null ||
    filters.attendeeId !== null ||
    filters.speakerIds.length > 0 ||
    filters.startDate !== null ||
    filters.endDate !== null
  )
}

export type ExtendResult = { status: 'EXTENDED' | 'NOT_EXTENDED' | 'ERROR'; leaveDate: string | null }
export type ExtendPreview = { status: 'EXTENDABLE' | 'NOT_EXTENDABLE' | 'ERROR'; leaveDate: string | null }

export type ClientMeetingOwner = {
  userId: number
  firstName: string
  lastName: string
  email: string
  isSetToJoin: boolean
}

/**
 * The things a person may be to a meeting, in the order the dropdown offers them.
 *
 * `OWNER` is not a stored access level: an owner is a meeting owner and the rest are share levels. The
 * backend resolves that split, so the dialog reads and writes one role per person.
 */
export const MEETING_ACCESS_ROLES = ['OWNER', 'EDITOR', 'REVIEWER', 'COMMENTER', 'RESTRICTED'] as const

export type MeetingAccessRole = (typeof MEETING_ACCESS_ROLES)[number]

/** The access levels a meeting may be shared at: Restricted only where its meeting, team or client allows it. */
export function filterOfferedAccessLevels<T extends string>(levels: readonly T[], allowRestrictedUsers: boolean): T[] {
  return levels.filter((level) => allowRestrictedUsers || level !== 'RESTRICTED')
}

export type ClientMeetingAccess = {
  userId: number
  firstName: string
  lastName: string
  email: string
  role: MeetingAccessRole
  isSetToJoin: boolean
}

// Restated rather than taken from ClientMeetingDto: that file names the Prisma enums, which the UI
// build cannot resolve, and the types shared across the boundary are import-free by convention.
/** A meeting's status as the admin meetings page reports and filters it, rather than the pipeline's own. */
export type MeetingReportingStatus = 'SCHEDULED' | 'NOT_JOINING' | 'RECORDING' | 'PROCESSING' | 'RECORDED' | 'NOT_RECORDED' | 'CANCELLED' | 'SOFT_DELETED'

export type ClientMeeting = {
  id: number
  name: string
  teamId: number
  teamName: string
  date: Date
  endDate: Date
  duration: number | null
  status: MeetingStatus
  reportingStatus: MeetingReportingStatus
  type: MeetingType
  latestMeetingSessionStatus: MeetingSessionStatus | null
  processingStatus: MeetingProcessingStatus | null
  users: ClientMeetingOwner[]
  access: ClientMeetingAccess[]
  markedToBeDeletedAt: Date | null
  deletionDate: Date | null
  isOngoing: boolean
  isCompleted: boolean
  actualEndDate: Date | null
  isEligibleForRecoveryAssistant: boolean
  canActOnMeeting: boolean
  allowRestrictedUsers: boolean
  canExtendRetentionPeriod: boolean
  softDeletionDate: Date | null
  maximumDeletionDate: Date | null
  maximumSoftDeletionDate: Date | null
  maximumSoftDeletionDateInTotal: Date | null
  maxSingleExtensionInDays: number | null
  maxTotalExtensionInDays: number | null
  usedTotalExtensionInDays: number
  minimumSoftDeletionDate: Date | null
  minimumDeletionDate: Date | null
  retentionExtensionCount: number
}

export type ClientMeetingQueryResult = { list: ClientMeeting[]; total: number }

export type MeetingRetentionExtension = {
  id: number
  createdAt: Date
  extendedByName: string
  /** Both null on an extension an administrator made on their own initiative rather than on request. */
  requestedByName: string | null
  requestedAt: Date | null
  deletionDate: Date
  previousDeletionDate: Date | null
  /** Both null on the entries written before the two deletion dates could be moved apart. */
  softDeletionDate: Date | null
  previousSoftDeletionDate: Date | null
}

export type PendingRetentionExtensionRequest = {
  id: number
  createdAt: Date
  requestedByName: string
  extensionInDays: number
}

export type RetentionExtensionRequestOptions = {
  isEnabled: boolean
  availableExtensionsInDays: number[]
  pendingRequest: PendingRetentionExtensionRequest | null
}

export type RetentionExtensionRequestStatus = 'PENDING' | 'APPROVED' | 'DECLINED'

export type RetentionExtensionRequestReview = {
  id: number
  meetingId: number
  /** Null for a support user looking at another client's meeting. */
  meetingName: string | null
  meetingDate: Date
  createdAt: Date
  requestedByName: string
  extensionInDays: number
  status: RetentionExtensionRequestStatus
  decidedAt: Date | null
  decidedByName: string | null
  isMeetingSoftDeleted: boolean
  currentSoftDeletionDate: Date | null
  resultingSoftDeletionDate: Date | null
}

/** The extensions a meeting user may ask for, in the order the dialog offers them. */
export const REQUESTABLE_EXTENSIONS_IN_DAYS = [1, 3, 5, 7]

export type PastMeetingsType = 'ALL' | 'OWN_MEETINGS' | 'SHARED_WITH_ME'
export type MeetingListType = 'upcoming' | 'past' | 'upcoming-search' | 'past-search'
export type AgendaType = 'SINGLE' | 'MULTIPLE'
export type Sentence = {
  sentenceId: string
  content: string
  startTimeSeconds: number
  endTimeSeconds: number
}

export type TranscriptSpeakerSlot = {
  type: 'speaker'
  speaker: Speaker
  startTime: number
  sentences: Sentence[]
  duration: number
}

export type TranscriptPauseSlot = {
  type: 'pause'
  startTime: number
  endTime: number
  actualDuration: number
}

export type TranscriptSlot = TranscriptSpeakerSlot | TranscriptPauseSlot

export type Transcript = TranscriptSlot[]

export type ActionItem = {
  id: number
  item: string
  assignee: string | null
  deadline: string | null
}

export type Challenge = {
  id: number
  item: string
  reason: string | null
  person: string | null
}

export type OutlineItem = {
  id: number
  title: string
  startTimeSeconds: number
  startSentenceId: string
  endSentenceId: string
  discussion: string
  actionItems: ActionItem[]
  challenges: Challenge[]
  decisions: string[]
}

export type SearchMatch = {
  sentence: string
  sentenceSearchId: string
  speakerId: string
  speakerName: string | null
  startTimeSeconds: number
}

export type MeetingAccessLevel = 'COMMENTER' | 'REVIEWER' | 'EDITOR' | 'RESTRICTED' | 'FULL_ACCESS'
export type MeetingAttendanceType = 'INTERNAL' | 'EXTERNAL'

export type MeetingShare = {
  sharedUser: User
  sharedUserId: number
  level: MeetingAccessLevel
}

export type Speaker = {
  speakerId: string
  name: string | null
  speakerTag: string
  startTimeSeconds: number
  originalTag: string | null
}

export type MeetingRoomScore = {
  roomName: string
  averageConfidenceScore: number | null
  averageRms: number
  averageHfRatio: number
  totalSpeakingTimeSeconds: number
  physicalSpeakerTags: string[]
}

export type RecentInteraction = {
  id: number
  date: Date
  provider: MeetingProvider
  meetingName: string
}

export type MeetingInviteStatus = 'ACCEPT' | 'TENTATIVE' | 'DECLINE'

export type GroupAttendee = {
  email: string
  name: string
  response: MeetingInviteStatus
  isOrganizer: boolean
}

export type GroupedAttendeeTeam = {
  teamName: string
  attendees: GroupAttendee[]
}

export type MeetingResourceType =
  | 'USER_UPLOADED_VIDEO'
  | 'USER_UPLOADED_AUDIO'
  | 'ASSISTANT_RECORDED_VIDEO'
  | 'ASSISTANT_RECORDED_AUDIO'
  | 'DISPLAY_VIDEO'
  | 'DISPLAY_AUDIO'
  | 'PROCESSED_AUDIO'
  | 'RESIZED_THUMBNAIL'
  | 'ORIGINAL_THUMBNAIL'
  | 'HALO_SPEAKERS'
  | 'AGENDA'
  | 'CUSTOM_DOCUMENT'
  | 'BOARD_PACK'

export type MeetingResource = {
  id: number
  createdAt: string
  updatedAt: string
  meetingId: number
  resourceType: MeetingResourceType
  fileName: string
  extension: string
  cloudStorageFileName: string
  index: number
  deletedAt: string | null
  downloadUrl: string | null
  deletionReason: string | null
  uploadedByUser: boolean
  uploadedByIngestBoardPackJob: boolean
  size: number
  metadata: MeetingResourceMetadata | null
}

export type MeetingResourceWithUrl = MeetingResource & {
  downloadUrl: string | null
}

export type MeetingResourceMetadata = {
  numberOfPages: number
  tokenCount: number | null
}

export type AdminMeetingResourceMetadata = {
  numberOfPages: number
  tokenCount: number | null
  hasDescription: boolean
  keywordCount: number
}

export type AdminMeetingResource = Omit<MeetingResource, 'metadata' | 'fileName'> & {
  fileName: string | null
  metadata: AdminMeetingResourceMetadata | null
}

export type PauseInterval = {
  start: number
  end: number
  actualDuration: number
}

export type RecordingClockAnchor = {
  recordingPosition: number
  date: string
}

export type SpeakerMapping = { [key: string]: string }
export type MinutesGenerationTemplateProgress = { level: string; index: number; total: number }

export type SendRecoveryAssistantResult = {
  outcome: 'APPENDED' | ResendAssistantResult['outcome']
}

const SEND_RECOVERY_ASSISTANT_TOASTS: Record<SendRecoveryAssistantResult['outcome'], string> = {
  APPENDED: 'MEETINGS.ADMIN.SEND_RECOVERY_ASSISTANT.SUCCESS',
  REQUEUED: 'MEETINGS.RESEND_ASSISTANT.SUCCESS',
  ALREADY_ON_ITS_WAY: 'MEETINGS.RESEND_ASSISTANT.ALREADY_ON_ITS_WAY',
}

export type ResendAssistantResult = {
  outcome: 'REQUEUED' | 'ALREADY_ON_ITS_WAY'
}

/** One request to see a meeting outside the requester's own team, and how it was settled. */
export type MeetingAccessRequest = {
  id: number
  meetingId: number
  requestedByUserId: number
  requestedAt: Date | null
  grantedByUserId: number | null
  grantedAt: Date | null
  revokedByUserId: number | null
  revokedAt: Date | null
}

export type Meeting = {
  transcript: Transcript
  id: number
  name: string
  allowTranscriptDownload: boolean
  allowMinutesDownload: boolean
  allowOutlineDownload: boolean
  teamId: number
  teamName: string
  provider: string
  description: string | null
  status: MeetingStatus
  endReason: string | null
  errorReason: string | null
  context: string[]
  date: Date
  endDate: Date
  actualStartDate: Date
  actualEndDate: Date
  queuedAt: Date
  waitingToBeAdmittedAt: Date
  createdAt: Date
  duration: number | null
  thumbnailUrl: string | null
  assistantInstructedToJoinByOtherUsers: boolean
  videoUrl: string | null
  audioUrl: string | null
  isPrevious: boolean
  isOngoing: boolean
  isUpcoming: boolean
  isMinutesGenerationOngoing: boolean
  /**
   * Whether a regeneration of the minutes is under way, including while a mapping-first run is still waiting on
   * its agenda-to-transcript mapping. `isMinutesGenerationOngoing` covers only the minutes job itself, which
   * reads as not started for that whole wait.
   */
  isMinutesRegenerationInFlight: boolean
  minutesGenerationProgress: number | null
  minutesGenerationTemplateProgress: MinutesGenerationTemplateProgress | null
  /**
   * The template items an in-flight regeneration is rewriting, including while it is still waiting on the
   * agenda-to-transcript mapping that precedes it. The editor locks their agenda items and leaves the rest of
   * the minutes editable. Null when nothing is running, or when the run covers the whole template.
   */
  minutesGenerationTargetTemplateItemIds: number[] | null
  hasMinutes: boolean
  hasErrorEmailBeenSent: boolean
  hasSoftDeletionWarningEmailBeenSent: boolean
  hasSoftDeletionConfirmationEmailBeenSent: boolean
  hasUpcomingMeetingReminderEmailBeenSent: boolean
  hasNativeFallbackEmailBeenSent: boolean
  transcriptTokenCount: number | null
  matches?: SearchMatch[] // TODO Lazy typing as the types for search and normal meeting showing are different...
  accessLevel: MeetingAccessLevel
  attendanceType: MeetingAttendanceType
  isSetToJoin: boolean
  notifyWhenPostProcessingCompletes: boolean
  userSpeakerMapping: SpeakerMapping | null
  automatedSpeakerMapping: SpeakerMapping | null
  processingStatus: MeetingProcessingStatus | null
  processingProgress: number | null
  hangUpKey: string | null
  haveSpeakersBeenUpdated: boolean
  isMinutingUpToDate: boolean
  isOutlineUpToDate: boolean
  usePartialMinutesRegeneration: boolean
  hasRestrictedMinuteItems: boolean
  /** Whether the meeting may be shared with Restricted access, resolved through the meeting, team and client. */
  allowRestrictedUsers: boolean
  allowMeetingEntities: boolean
  allowActionItems: boolean
  counterpartyNames: string[]
  currentParticipantCount: number | null
  link: string | null
  isSpeechMissing: boolean
  isPostProcessingComplete: boolean
  isTranscriptionComplete: boolean
  allowMediaDownload: boolean
  markedToBeDeletedAt: Date | null
  calendarSyncLostAt: Date | null
  isTranscriptReady: boolean
  isMediaReady: boolean
  isOutlineReady: boolean
  physicalSpeakersExist: boolean
  type: MeetingType
  medium: MeetingMedium
  attendeeNames: string[]
  hasVirtualSpeakers: boolean
  participantCount: number
  meetingProfileId: number | null
  hasWarningForMissingAgendaBeenShown: boolean
  haveMinuteStylesBeenUpdatedSinceLastProcessing: boolean
  totalNumberOfSplits: number
  templateId: number | null
  meetingSetIndex: number | null
  meetingSetSize: number | null
  finalDeletionDate: Date | null
  retentionPeriodExpirationDate: Date | null
  retentionPeriodInDaysForMeeting: number | null
  mediaDeletionDate: Date | null
  documentDeletionDate: Date | null
  transcriptDeletionDate: Date | null
  avgConfidenceScore: number | null

  outlineItems: OutlineItem[]
  shares: MeetingShare[]
  users: User[]
  speakers: Speaker[]
  recentInteractions: RecentInteraction[]
  groupedAttendees: GroupedAttendeeTeam[]

  agendaDownloadUrl: string | null
  userUploadedResources: MeetingResourceWithUrl[]
  uploadedRecording: MeetingResource | null
  agendaType: AgendaType
  templateSourceType: TemplateSourceType | null
  pauseIntervals: PauseInterval[]
  recordingClockAnchors: RecordingClockAnchor[] | null
  hasEditorDocument: boolean
  hasOnlyLegacyMinutes: boolean

  minutesCitations: CitationMinuteNodes | null
  agendaMetadata: AdminAgendaMetadata
  agendaExtractionProgress: number | null
  agendaExtractionFailed: boolean
  invalidAgendaReason: InvalidAgendaReason | null
  isCheckingAgendaValidity: boolean
  hasTemplateItems: boolean
  isAgendaExtractionInProgress: boolean
  isIngestingBoardPack: boolean
  boardPackIngestionProgress: number | null
  boardPackIngestionFailed: boolean
  templateApprovedByUser: boolean
  isParticipantExtractionInProgress: boolean
  lastSpeechEndTime: number | null
  transcriptLanguage: string | null
  allowSystemGuidanceEditing: boolean
} & MinutesStyling

export type MeetingWithLatestSession = Meeting & {
  latestMeetingSession: MeetingSession | null
}

export type MeetingTimelineEvent = {
  type: string
  timestamp: Date
  userName: string | null
  metadata: any | null
}

export type MeetingTimelineUserActivity = {
  userName: string
  hasEnabledAssistant: boolean
  viewCount: number
  lastViewedAt: Date | null
  lastEnabledAt: Date | null
  lastDisabledAt: Date | null
}

export type MeetingTimelineResponse = {
  events: MeetingTimelineEvent[]
  userActivities: MeetingTimelineUserActivity[]
}

export type AdminOutlineStructureItem = {
  id: number
  actionItemCount: number
  challengeCount: number
  decisionCount: number
  startTimeSeconds: number
  hasError: boolean
}

export type AdminOutlineStructure = {
  items: AdminOutlineStructureItem[]
}

export enum AgendaAttendeeType {
  CHAIR = 'CHAIR',
  MEMBER = 'MEMBER',
  STANDING_ATTENDEE = 'STANDING_ATTENDEE',
  ATTENDEE_BY_INVITATION = 'ATTENDEE_BY_INVITATION',
  OBSERVER = 'OBSERVER',
  COMPANY_SECRETARY = 'COMPANY_SECRETARY',
  APOLOGY = 'APOLOGY',
  OTHER = 'OTHER',
}

export enum GoverningBodyType {
  BOARD = 'BOARD',
  COMMITTEE = 'COMMITTEE',
  OTHER = 'OTHER',
}

export enum TemplateSourceType {
  UPLOAD = 'UPLOAD',
  CREATE = 'CREATE',
  LOAD = 'LOAD',
}

export function computeIsCheckingAgendaValidity(meeting: Pick<Meeting, 'isCheckingAgendaValidity'>): boolean {
  return meeting.isCheckingAgendaValidity
}

export function isAgendaExtractionRejectionVisible(meeting: Pick<Meeting, 'invalidAgendaReason' | 'agendaExtractionFailed' | 'templateSourceType'>): boolean {
  return (meeting.invalidAgendaReason !== null || meeting.agendaExtractionFailed) && meeting.templateSourceType === TemplateSourceType.UPLOAD
}

export type AgendaUploadStatus = 'idle' | 'uploading' | 'checking' | 'failed' | 'uploaded'

export function computeAgendaUploadStatus(params: { isImporting: boolean; isFailed: boolean; isChecking: boolean; isUploaded: boolean }): AgendaUploadStatus {
  if (params.isImporting) {
    return 'uploading'
  }
  if (params.isFailed) {
    return 'failed'
  }
  if (params.isChecking) {
    return 'checking'
  }
  if (params.isUploaded) {
    return 'uploaded'
  }
  return 'idle'
}

export function computeAgendaFailureMessageKey(invalidAgendaReason: InvalidAgendaReason | null, agendaExtractionFailed: boolean): string | null {
  if (invalidAgendaReason !== null) {
    return `MEETING_SETUP.TEMPLATE_REVIEW.INVALID_AGENDA_REASONS.${invalidAgendaReason}`
  }
  if (agendaExtractionFailed) {
    return 'MEETING_SETUP.TEMPLATE_REVIEW.EXTRACTION_FAILED'
  }
  return null
}

export function computeHasUsableTemplateSource(params: {
  hasAgendaUploaded: boolean
  templateSourceType: TemplateSourceType | null
  isCheckingAgendaValidity: boolean
  isImportingAgenda: boolean
  isAgendaExtractionFailed: boolean
}): boolean {
  const hasTemplateSource = params.hasAgendaUploaded || params.templateSourceType !== null
  if (!hasTemplateSource) {
    return false
  }
  return !params.isCheckingAgendaValidity && !params.isImportingAgenda && !params.isAgendaExtractionFailed
}

export function computeShouldConfirmTemplateReplace(params: {
  templateSourceType: TemplateSourceType | null
  isCheckingAgendaValidity: boolean
  isImportingAgenda: boolean
  isAgendaExtractionFailed: boolean
  isCreateTemplateSelected: boolean
  templateItemsCount: number
}): boolean {
  if (params.templateSourceType === null) {
    return false
  }
  if (params.isCheckingAgendaValidity || params.isImportingAgenda || params.isAgendaExtractionFailed) {
    return false
  }
  if (params.isCreateTemplateSelected && params.templateItemsCount === 0) {
    return false
  }
  return true
}

export function computeShouldShowTemplateSourcePicker(params: {
  isCheckingAgendaValidity: boolean
  isAgendaExtractionFailed: boolean
  hasTemplateItems: boolean
  itemCount: number
  templateSourceType: TemplateSourceType | null
}): boolean {
  if (params.isCheckingAgendaValidity) {
    return true
  }
  if (params.isAgendaExtractionFailed && !params.hasTemplateItems) {
    return true
  }
  return params.itemCount === 0 && params.templateSourceType === null
}

export function computeCurrentStep(params: { isUploadedMeeting: boolean; isMultipleAgenda: boolean; hasUsableTemplateSource: boolean }): number {
  if (params.isUploadedMeeting) {
    return params.hasUsableTemplateSource ? 4 : 2
  }
  if (params.isMultipleAgenda) {
    return 3
  }
  return params.hasUsableTemplateSource ? 4 : 3
}

export function computeIsTemplateReviewVisible(params: {
  isMultipleAgenda: boolean
  currentStep: number
  reviewStepNumber: number
  isPopulatingTemplate: boolean
  isIngestingBoardPack: boolean
  isImportingAgenda: boolean
  isAgendaExtractionInProgress: boolean
  hasMeeting: boolean
  hasMetadata: boolean
}): boolean {
  const hasReachedReviewStep = params.currentStep >= params.reviewStepNumber && !params.isAgendaExtractionInProgress
  return (
    !params.isMultipleAgenda &&
    !params.isImportingAgenda &&
    (hasReachedReviewStep || params.isPopulatingTemplate || params.isIngestingBoardPack) &&
    params.hasMeeting &&
    params.hasMetadata
  )
}

export type AdminAgendaAttendee = {
  id: number
  firstName: string
  lastName: string
  role: string | null
  entity: string | null
  type: AgendaAttendeeType
  initials: string | null
  honorifics: string | null
}

export type AdminAgendaMetadata = {
  id: number
  companyName: string | null
  location: string | null
  date: string | null
  time: string | null
  entities: string[]
  committeeType: string | null
  governingBodyType: GoverningBodyType | null
  attendees: AdminAgendaAttendee[]
}

export type MeetingOwner = {
  user: User
  isSetToJoin: boolean
  isSetToJoinFrozenByUser: boolean
  notifyWhenPostProcessingCompletes: boolean
  hasWeekendDeletionReminderEmailBeenSent: boolean
  isPrimaryCalendar: boolean
}

export type MeetingSession = {
  index: number
  actualStartDate: Date | null
  actualEndDate: Date | null
  status: MeetingSessionStatus
  duration: number | null
  errorReason: string | null
  endReason: string | null
  assistantErrorReason: string | null
  waitingToBeAdmittedAt: Date | null
  lastHeartbeatReceivedAt: Date | null
  pauseIntervals: PauseInterval[]
  originalSplitOffsets: number[]
  adjustedSplitOffsets: number[]
  cloudRunExecutionName: string | null
  invoker: string | null
  useNativeAssistant: boolean
}

export type MeetingSessionWithSegments = MeetingSession & {
  segments: MeetingSegment[]
}

export type MeetingSegment = {
  meetingId: number
  sessionIndex: number
  index: number
  startOffset: number
  duration: number
}

export type AnonymizedMeetingParticipation = {
  joinedAt: Date
  leftAt: Date | null
}

export type AnonymizedMeetingParticipant = {
  id: number
  consentResponse: ConsentResponse | null
  consentRequestedAt: Date | null
  consentRespondedAt: Date | null
  participations: AnonymizedMeetingParticipation[]
}

export type InvalidAgendaReason = 'NO_READABLE_TEXT' | 'NO_AGENDA_ITEMS' | 'BOARD_PACK' | 'MINUTES_NOT_AGENDA' | 'OTHER'

export type MeetingBatchJobRun = {
  id: number
  type: BatchJobType
  task: string | null
  registeredAt: Date | null
  responseAt: Date | null
  successful: boolean | null
  error: string | null
  metadata: any
  meetingId: number
  completionPercentage: number
  executionGroupId: string
  triggeredBy: string
  isDry: boolean
  targetTemplateItemIds: number[]
}

export type MeetingBatchJobRunWithDuration = MeetingBatchJobRun & {
  duration: number
  agendaTranscriptMapping?: AgendaTranscriptMappingResponse | null
}

export type MeetingBatchJobDetails = {
  singleRuns: MeetingBatchJobRunWithDuration[]
}

export type InProgressBatchJob = MeetingBatchJobRunWithDuration & {
  teamName: string
}

export type UploadedDocumentInfo = {
  resourceType: 'AGENDA' | 'CUSTOM_DOCUMENT' | 'BOARD_PACK'
  fileName: string
  size: number
}

export type DebugDataFile = {
  key: string
  fileName: string
  url: string
  lastModified: string
  mediaType: 'VIDEO' | 'IMAGE' | 'OTHER'
}

export type EditorDebugRevision = {
  id: number
  userId: number | null
  hasContent: boolean
  hasContentRaw: boolean
  createdAt: string
  updatedAt: string
}

export type EditorDebugLoadingInputs = {
  isPostProcessingComplete: boolean
  isMinutesGenerationOngoing: boolean
  isMinutesGenerationTargeted: boolean
  minutesGenerationProgress: number | null
  isSpeechMissing: boolean
}

export type EditorDebugSyncReadiness = {
  hasPersistedYDocument: boolean
  hasAnyRevision: boolean
}

export type EditorAdminDebug = {
  revisions: EditorDebugRevision[]
  loadingInputs: EditorDebugLoadingInputs
  syncReadiness: EditorDebugSyncReadiness
}

export type HallucinationAuditSegment = {
  start: number
  end: number
  avg_logprob: number | null
  word_count: number
  word_score_min: number | null
  word_score_mean: number | null
  text: string | null
  sentenceId: string | null
}

export type HallucinationAuditData = {
  meetingId: number
  jobId: number
  artifactMissing: boolean
  segments: HallucinationAuditSegment[]
}

export type AgendaTranscriptMappingSegment = {
  startSeconds: number
  endSeconds: number
  confidence: 'HIGH' | 'LOW'
}

export type AgendaTranscriptMappingItem = {
  agendaItemId: number | null
  itemMappingId: number | null
  agendaItemTitle?: string
  itemNumber: string
  depth: number
  topLevelAncestorId: number | null
  hasChildren: boolean
  contentBlockTypes: string[]
  orderIndex: number
  status: 'DISCUSSED' | 'NOT_DISCUSSED' | 'NO_DISCUSSION_FOUND'
  segments: AgendaTranscriptMappingSegment[]
}

export type AgendaTranscriptMappingUnmappedRegion = {
  startSeconds: number
  endSeconds: number
  kind: 'MAPPING_GAP' | 'SILENCE'
}

export type AgendaTranscriptMappingUnmappedDiscussionRange = {
  startSeconds: number
  endSeconds: number
}

export type UpdateAgendaTranscriptMappingItem = {
  itemMappingId: number
  status: 'DISCUSSED' | 'NOT_DISCUSSED' | 'NO_DISCUSSION_FOUND'
  segments: AgendaTranscriptMappingSegment[]
}

export type AgendaTranscriptMappingResponse = {
  meetingId: number
  transcriptDurationSeconds: number
  isManuallyEdited: boolean
  isDry: boolean
  agendaItems: AgendaTranscriptMappingItem[]
  unmappedDiscussionRanges: AgendaTranscriptMappingUnmappedDiscussionRange[]
  unmappedRegions: AgendaTranscriptMappingUnmappedRegion[]
}

export type AgendaTranscriptMappingMode = 'DISABLED' | 'ENABLED' | 'ENABLED_HIGH_ONLY'

export type MinutingAgent = 'CONDENSATION' | 'COLLOQUIALISM' | 'NAMING'

export const MINUTING_AGENTS: MinutingAgent[] = ['CONDENSATION', 'COLLOQUIALISM', 'NAMING']

// Agents that always run and cannot be toggled off in the UI. Additional agents
// added later stay optional and remain selectable.
export const MANDATORY_MINUTING_AGENTS: MinutingAgent[] = ['CONDENSATION', 'COLLOQUIALISM', 'NAMING']

export type AnonymizedTemplateContentBlockSource = {
  type: TemplateContentBlockSourceType
  id: number | null
  fileName: string | null
}

export type AnonymizedBinaryOption = {
  placeholderCount: number
}

export type AnonymizedTemplateContentBlock = {
  type: TemplateContentBlockType
  levelOfDetail: MinutesVerboseness
  anonymization: MinutesAnonymization
  hasGuidance: boolean
  sources: AnonymizedTemplateContentBlockSource[]
  placeholderCount: number | null
  binaryOptions: AnonymizedBinaryOption[] | null
}

export type AnonymizedTemplateItem = {
  id: number
  index: number
  title: string | null
  presenterCount: number
  duration: number | null
  isSectionHeading: boolean
  hasSemanticSummary: boolean
  hasKeyVocabulary: boolean
  contentBlocks: AnonymizedTemplateContentBlock[]
  minuteTimelineSegments: MinuteTimelineSegment[]
  hasMinuteContent: boolean
  subitems: AnonymizedTemplateItem[]
}

export type AdminMeetingDetails = Pick<
  Meeting,
  | 'id'
  | 'status'
  | 'isSetToJoin'
  | 'date'
  | 'endDate'
  | 'actualStartDate'
  | 'actualEndDate'
  | 'createdAt'
  | 'speakers'
  | 'provider'
  | 'type'
  | 'isOngoing'
  | 'shares'
  | 'duration'
  | 'processingStatus'
  | 'userSpeakerMapping'
  | 'automatedSpeakerMapping'
  | 'haveSpeakersBeenUpdated'
  | 'markedToBeDeletedAt'
  | 'calendarSyncLostAt'
  | 'medium'
  | 'isSpeechMissing'
  | 'isMediaReady'
  | 'isTranscriptReady'
  | 'isOutlineReady'
  | 'isPostProcessingComplete'
  | 'endReason'
  | 'errorReason'
  | 'participantCount'
  | 'currentParticipantCount'
  | 'meetingProfileId'
  | 'agendaType'
  | 'templateId'
  | 'finalDeletionDate'
  | 'retentionPeriodExpirationDate'
  | 'retentionPeriodInDaysForMeeting'
  | 'mediaDeletionDate'
  | 'documentDeletionDate'
  | 'transcriptDeletionDate'
  | 'lastSpeechEndTime'
  | 'hasErrorEmailBeenSent'
  | 'hasSoftDeletionWarningEmailBeenSent'
  | 'hasSoftDeletionConfirmationEmailBeenSent'
  | 'hasUpcomingMeetingReminderEmailBeenSent'
  | 'hasNativeFallbackEmailBeenSent'
> &
  MinutesStyling & {
    isOrganizedByThisTeam: boolean
    attendeeCountsByGuestOrganization: { teamName: string; attendeeCount: number; hasOrganizer: boolean }[]
    agendaAttendees: Pick<AdminAgendaAttendee, 'id' | 'type'>[]
    attendance: AnonymizedMeetingParticipant[]
    contextEntryCount: number
    outlineStructure: AdminOutlineStructure
    meetingOwners: MeetingOwner[]
    batchJobRuns: MeetingBatchJobDetails
    speakersFileDownloadUrl: string | null
    spectrogramUrl: string | null
    allResources: AdminMeetingResource[]
    team: Team
    templateSourceType: TemplateSourceType | null
    anonymizedTemplateItems: AnonymizedTemplateItem[] | null
    useNativeAssistant: boolean | null
    minutingAgents: MinutingAgent[]
    overrideTeamLevelMinutingAgents: boolean
    useVerificationDiscovery: boolean | null
    allowSystemGuidanceEditing: boolean
    useAgendaTranscriptMapping: AgendaTranscriptMappingMode | null
    anonymiseMemberSpeakers: boolean | null
    useTranscriptRefinement: boolean | null
    allowRestrictedUsers: boolean | null
    isMinutingUpToDate: boolean
    isOutlineUpToDate: boolean
    isAgendaTranscriptMappingUpToDate: boolean
    meetingSessions: MeetingSessionWithSegments[]
    latestMeetingSession: MeetingSession | null
    uploadedDocuments: UploadedDocumentInfo[]
    audioMetrics: {
      speakerDiagnostics: {
        segmentMetrics: {
          speaker: string
          startTimeSeconds: number
          endTimeSeconds: number
          durationSeconds: number
          rms: number
          peak: number
          hfRatio: number
          silenceRatio: number
          meanConfidenceScore?: number
          index: number
        }[]
        speakerMetrics: {
          [speaker: string]: {
            speaker: string
            totalSpeakingTimeSeconds: number
            segmentCount: number
            avgRms: number
            avgPeak: number
            avgHfRatio: number
            avgSilenceRatio: number
            avgConfidenceScore?: number
            haloSpeakerName?: string | null
          }
        }
        meetingMetrics: {
          totalDurationSeconds: number
          totalSegments: number
          totalSpeakers: number
          segmentsPerMinute: number
          avgRms: number
          avgPeak: number
          avgSilenceRatio: number
          avgHfRatio: number
          avgConfidenceScore?: number
        }
      } | null
    }
    avgConfidenceScore: number | null
    avgRms: number | null
    meetingRoomScores: MeetingRoomScore[]
    virtualSpeakers: Speaker[] | null
    virtualSpeakerMapping: SpeakerMapping | null
    keyName: string | null
  }

export type SearchResponse = Meeting[]

export type MeetingListResponse = {
  meetings: Meeting[]
  hasMore: boolean
  nextCursor?: number
}

export enum PostProcessor {
  TRANSCODER = 'TRANSCODER',
  ASR = 'ASR',
  LLM_SPEAKER_LABELING = 'LLM_SPEAKER_LABELING',
  OUTLINE = 'OUTLINE',
  AGENDA_EXTRACTION = 'AGENDA_EXTRACTION',
  MINUTES = 'MINUTES',
  TRANSCODER_SPLIT = 'TRANSCODER_SPLIT',
  MAP_AGENDA_TO_TRANSCRIPT = 'MAP_AGENDA_TO_TRANSCRIPT',
  INGEST_BOARD_PACK = 'INGEST_BOARD_PACK',
  REFINE_TRANSCRIPT = 'REFINE_TRANSCRIPT',
  ACTION_ITEMS = 'ACTION_ITEMS',
}

export type LlmModel =
  | 'ANTHROPIC_CLAUDE_4_5_OPUS'
  | 'ANTHROPIC_CLAUDE_4_5_HAIKU'
  | 'ANTHROPIC_CLAUDE_4_6_OPUS'
  | 'ANTHROPIC_CLAUDE_4_6_SONNET'
  | 'ANTHROPIC_CLAUDE_4_7_OPUS'
  | 'ANTHROPIC_CLAUDE_5_FABLE'
  | 'ANTHROPIC_CLAUDE_5_5_OPUS'
  | 'ANTHROPIC_CLAUDE_5_SONNET'
  | 'ANTHROPIC_CLAUDE_5_5_HAIKU'
  | 'GOOGLE_GEMINI_2_5_PRO'

export const LLM_MODELS: LlmModel[] = [
  'ANTHROPIC_CLAUDE_4_5_OPUS',
  'ANTHROPIC_CLAUDE_4_5_HAIKU',
  'ANTHROPIC_CLAUDE_4_6_OPUS',
  'ANTHROPIC_CLAUDE_4_6_SONNET',
  'ANTHROPIC_CLAUDE_4_7_OPUS',
  'ANTHROPIC_CLAUDE_5_FABLE',
  'ANTHROPIC_CLAUDE_5_5_OPUS',
  'ANTHROPIC_CLAUDE_5_SONNET',
  'ANTHROPIC_CLAUDE_5_5_HAIKU',
  'GOOGLE_GEMINI_2_5_PRO',
]

export type LlmEffort = 'LOW' | 'MEDIUM' | 'HIGH' | 'EXTRA_HIGH' | 'MAX'

export const LLM_EFFORTS: LlmEffort[] = ['LOW', 'MEDIUM', 'HIGH', 'EXTRA_HIGH', 'MAX']

type Attendee = {
  name: string
  email: string
}

export enum MinutesVerboseness {
  CONCISE = 'CONCISE',
  DETAILED = 'DETAILED',
  EXHAUSTIVE = 'EXHAUSTIVE',
}

export enum MinutesAnonymization {
  ALL = 'ALL',
  NONE = 'NONE',
}

export enum MinutesPassive {
  ACTIVE = 'ACTIVE',
  CONTEXTUAL = 'CONTEXTUAL',
  PASSIVE = 'PASSIVE',
}

export enum MinutesNameFormat {
  FIRST_NAME = 'FIRST_NAME',
  ROLE = 'ROLE',
  SURNAME = 'SURNAME',
  HONORIFIC_SURNAME = 'HONORIFIC_SURNAME',
  INITIALS = 'INITIALS',
  FULL_NAME = 'FULL_NAME',
  INITIAL_SURNAME = 'INITIAL_SURNAME',
}

export enum MinutesDateFormat {
  DD_MONTH_YYYY = 'DD_MONTH_YYYY',
  MONTH_DD_TH_YYYY = 'MONTH_DD_TH_YYYY',
  DD_MM_YYYY_DOT = 'DD_MM_YYYY_DOT',
  DD_MM_YYYY_SLASH = 'DD_MM_YYYY_SLASH',
}

export enum MinutesTimeFormat {
  TWENTY_FOUR_HOUR = 'TWENTY_FOUR_HOUR',
  TWELVE_HOUR = 'TWELVE_HOUR',
}

export enum MinutesLanguage {
  BRITISH = 'BRITISH',
  AMERICAN = 'AMERICAN',
  SPANISH = 'SPANISH',
  FRENCH = 'FRENCH',
}

export enum MinutesAttribution {
  OUTCOME = 'OUTCOME',
  COLLECTIVE = 'COLLECTIVE',
  CONTEXTUAL = 'CONTEXTUAL',
}

export enum MeetingMedium {
  PHYSICAL = 'PHYSICAL',
  VIRTUAL = 'VIRTUAL',
}

export enum MetricType {
  OPEN_APP = 'OPEN_APP',
  VIEW_MEETING = 'VIEW_MEETING',
  RUN_LLM_LIVE_ACTION = 'RUN_LLM_LIVE_ACTION',
  COMPLETE_LLM_LIVE_ACTION = 'COMPLETE_LLM_LIVE_ACTION',
  SAVE_EDITOR_CONTENT = 'SAVE_EDITOR_CONTENT',
  COMMENT_ON_EDITOR = 'COMMENT_ON_EDITOR',
  CONNECT_CALENDAR = 'CONNECT_CALENDAR',

  UPLOAD_MEETING = 'UPLOAD_MEETING',
  ADD_MANUAL_MEETING = 'ADD_MANUAL_MEETING',
  CLICK_ON_UPCOMING_MEETING = 'CLICK_ON_UPCOMING_MEETING',
  TOGGLE_ON_MEETING = 'TOGGLE_ON_MEETING',
  TOGGLE_OFF_MEETING = 'TOGGLE_OFF_MEETING',

  CLICK_ON_OUTLINE_TAB = 'CLICK_ON_OUTLINE_TAB',
  CLICK_ON_OUTLINE_ITEM_TO_EXPAND = 'CLICK_ON_OUTLINE_ITEM_TO_EXPAND',
  CLICK_ON_OUTLINE_ITEM_TO_GO_TO_TRANSCRIPT = 'CLICK_ON_OUTLINE_ITEM_TO_GO_TO_TRANSCRIPT',
  CLICK_ON_EXPAND_ALL_OUTLINE_ITEMS = 'CLICK_ON_EXPAND_ALL_OUTLINE_ITEMS',
  CLICK_ON_COLLAPSE_ALL_OUTLINE_ITEMS = 'CLICK_ON_COLLAPSE_ALL_OUTLINE_ITEMS',

  CHANGE_MINUTING_STYLES = 'CHANGE_MINUTING_STYLES',
  CHANGE_AGENDA_TYPE = 'CHANGE_AGENDA_TYPE',

  CLICK_LLM_LIVE_ACTION_INSERTION_OF_SUGGESTION = 'CLICK_LLM_LIVE_ACTION_INSERTION_OF_SUGGESTION',

  TOGGLE_REVISION_HISTORY = 'TOGGLE_REVISION_HISTORY',
  RESTORE_REVISION = 'RESTORE_REVISION',

  SEARCH_WITH_KEYWORD = 'SEARCH_WITH_KEYWORD',
  SEARCH_WITH_FILTER_BY_TYPE = 'SEARCH_WITH_FILTER_BY_TYPE',
  CLICK_ON_MEETING_IN_SEARCH_RESULTS = 'CLICK_ON_MEETING_IN_SEARCH_RESULTS',
  CLICK_ON_TEXT_SNIPPET_IN_SEARCH_RESULTS = 'CLICK_ON_TEXT_SNIPPET_IN_SEARCH_RESULTS',

  CLICK_ON_SPEAKERS_XRAY_BUTTON = 'CLICK_ON_SPEAKERS_XRAY_BUTTON',
  ASSIGN_SPEAKER_VIA_XRAY = 'ASSIGN_SPEAKER_VIA_XRAY',
  ASSIGN_SPEAKER_VIA_TRANSCRIPT_SELECTION = 'ASSIGN_SPEAKER_VIA_TRANSCRIPT_SELECTION',
  ENABLE_SPEAKER_LABELING_MODE = 'ENABLE_SPEAKER_LABELING_MODE',
  DISABLE_SPEAKER_LABELING_MODE = 'DISABLE_SPEAKER_LABELING_MODE',

  UPLOAD_RESOURCE = 'UPLOAD_RESOURCE',
  DELETE_RESOURCE = 'DELETE_RESOURCE',

  PAUSE_RECORDING = 'PAUSE_RECORDING',
  RESUME_RECORDING = 'RESUME_RECORDING',

  CLICK_TRANSCRIPT_TO_REPLAY_VIDEO = 'CLICK_TRANSCRIPT_TO_REPLAY_VIDEO',
  CLICK_SKIP_TO_NEXT_SPEAKER_BUTTON = 'CLICK_SKIP_TO_NEXT_SPEAKER_BUTTON',

  DOWNLOAD_VIDEO_RECORDING = 'DOWNLOAD_VIDEO_RECORDING',
  DOWNLOAD_AUDIO_RECORDING = 'DOWNLOAD_AUDIO_RECORDING',
  DOWNLOAD_TRANSCRIPT = 'DOWNLOAD_TRANSCRIPT',
  DOWNLOAD_MINUTES = 'DOWNLOAD_MINUTES',
  DOWNLOAD_OUTLINE = 'DOWNLOAD_OUTLINE',
  DOWNLOAD_ACTION_ITEMS = 'DOWNLOAD_ACTION_ITEMS',

  JOIN_MEETING_NOW = 'JOIN_MEETING_NOW',

  CREATE_MEETING = 'CREATE_MEETING',
  UPDATE_MEETING = 'UPDATE_MEETING',
  POSTPONE_MEETING = 'POSTPONE_MEETING',
  CANCEL_MEETING = 'CANCEL_MEETING',

  DELETE_MEETING_SOFT = 'DELETE_MEETING_SOFT',
  DELETE_MEETING_HARD = 'DELETE_MEETING_HARD',

  SHARE_MEETING_WITH_EXISTING_USER = 'SHARE_MEETING_WITH_EXISTING_USER',
  SHARE_MEETING_WITH_NEW_USER = 'SHARE_MEETING_WITH_NEW_USER',
  REPROCESS_OUTPUTS = 'REPROCESS_OUTPUTS',

  ADD_AGENDA_ITEM = 'ADD_AGENDA_ITEM',
  DELETE_AGENDA_ITEM = 'DELETE_AGENDA_ITEM',
  UPDATE_AGENDA_ITEM = 'UPDATE_AGENDA_ITEM',
  RESET_AGENDA_ITEMS = 'RESET_AGENDA_ITEMS',

  LOAD_TEMPLATE = 'LOAD_TEMPLATE',
  CREATE_BLANK_TEMPLATE = 'CREATE_BLANK_TEMPLATE',
  ADD_TEMPLATE_ITEM = 'ADD_TEMPLATE_ITEM',
  REMOVE_TEMPLATE_ITEM = 'REMOVE_TEMPLATE_ITEM',
  MOVE_TEMPLATE_ITEM = 'MOVE_TEMPLATE_ITEM',
  EDIT_TEMPLATE_ITEM = 'EDIT_TEMPLATE_ITEM',
  ATTACH_TEMPLATE_DOCUMENT = 'ATTACH_TEMPLATE_DOCUMENT',
  DETACH_TEMPLATE_DOCUMENT = 'DETACH_TEMPLATE_DOCUMENT',

  REGISTER_MEETING = 'REGISTER_MEETING',
  HANGUP_MEETING = 'HANGUP_MEETING',

  JOIN_MEETING = 'JOIN_MEETING',
  LEAVE_MEETING = 'LEAVE_MEETING',

  SEND_EMAIL = 'SEND_EMAIL',
  MARK_MEETING_AS_ERRORED = 'MARK_MEETING_AS_ERRORED',

  SEND_TO_TRANSCODING = 'SEND_TO_TRANSCODING',
  COMPLETE_TRANSCODING = 'COMPLETE_TRANSCODING',
  RECEIVE_TRANSCODER_AUDIO_READY = 'RECEIVE_TRANSCODER_AUDIO_READY',

  SEND_TO_TRANSCRIPTION = 'SEND_TO_TRANSCRIPTION',
  COMPLETE_TRANSCRIPTION = 'COMPLETE_TRANSCRIPTION',

  SEND_TO_OUTLINE_GENERATION = 'SEND_TO_OUTLINE_GENERATION',
  COMPLETE_OUTLINE_GENERATION = 'COMPLETE_OUTLINE_GENERATION',

  SEND_TO_MINUTES_GENERATION = 'SEND_TO_MINUTES_GENERATION',
  COMPLETE_MINUTES_GENERATION = 'COMPLETE_MINUTES_GENERATION',
  MINUTES_GENERATION_INPUT_TOKENS = 'MINUTES_GENERATION_INPUT_TOKENS',
  MINUTES_GENERATION_OUTPUT_TOKENS = 'MINUTES_GENERATION_OUTPUT_TOKENS',

  SEND_TO_AGENDA_EXTRACTION = 'SEND_TO_AGENDA_EXTRACTION',
  COMPLETE_AGENDA_EXTRACTION = 'COMPLETE_AGENDA_EXTRACTION',

  CREATE_USER = 'CREATE_USER',
  DELETE_USER = 'DELETE_USER',
  CREATE_TEAM = 'CREATE_TEAM',
  UPDATE_TEAM_SETTINGS = 'UPDATE_TEAM_SETTINGS',

  EDITOR_POLLING_FALLBACK = 'EDITOR_POLLING_FALLBACK',
}
export type MinutesStyling = {
  minutesVerboseness: MinutesVerboseness | null
  minutesAnonymization: MinutesAnonymization | null
  minutesPassive: MinutesPassive | null
  minutesNameFormat: MinutesNameFormat | null
  minutesDateFormat: MinutesDateFormat | null
  minutesTimeFormat: MinutesTimeFormat | null
  minutesLanguage: MinutesLanguage | null
  minutesAttribution: MinutesAttribution | null
}

export type UploadMeetingData = {
  name: string
  date: Date
  userIds: number[]
}

export type AddMeetingData = {
  link: string
  name: string
  userIds: number[]
  date: Date
  duration: number
}

export type ImportResourceData = {
  uploadKey: string
  targetResourceExtension: string
  fileName: string
  sourceDocumentFormat: 'PDF' | 'DOCX' | 'PPTX'
}

type AdminMeetingQueryResult = {
  list: AdminMeetingDetails[]
  total: number
}

export enum AgendaItemCategory {
  SUBSTANTIVE = 'SUBSTANTIVE',
  PROCEDURAL = 'PROCEDURAL',
}

export enum AgendaItemSubCategory {
  INTRODUCTION = 'INTRODUCTION',
  CHAIR_OF_THE_MEETING = 'CHAIR_OF_THE_MEETING',
  NOTICE_GIVEN_TO_DIRECTORS = 'NOTICE_GIVEN_TO_DIRECTORS',
  QUORUM = 'QUORUM',
  APOLOGIES_FOR_ABSENCE = 'APOLOGIES_FOR_ABSENCE',
  DECLARATIONS_OF_INTERESTS = 'DECLARATIONS_OF_INTERESTS',
  APPROVAL_OF_PREVIOUS_BOARD_MINUTES = 'APPROVAL_OF_PREVIOUS_BOARD_MINUTES',
  MATTERS_ARISING = 'MATTERS_ARISING',
  ANY_OTHER_BUSINESS = 'ANY_OTHER_BUSINESS',
  DATE_OF_NEXT_MEETING = 'DATE_OF_NEXT_MEETING',
  CLOSE_OF_MEETING = 'CLOSE_OF_MEETING',
  CHAIRS_UPDATE = 'CHAIRS_UPDATE',
  CHAIRS_INTRODUCTION = 'CHAIRS_INTRODUCTION',
  OTHER_PROCEDURAL = 'OTHER_PROCEDURAL',
  VERBAL_UPDATE = 'VERBAL_UPDATE',
  REPORT = 'REPORT',
  DECISIONS_APPROVALS = 'DECISIONS_APPROVALS',
  OTHER_SUBSTANTIVE = 'OTHER_SUBSTANTIVE',
}

export function hasUnlabeledSpeakers(meeting: Meeting): boolean {
  if (meeting.userSpeakerMapping === null) {
    return false
  }
  const sufficientlyLongTranscriptSpeakerSlots = (meeting.transcript.filter((slot) => slot.type === 'speaker') as TranscriptSpeakerSlot[]).filter(
    (slot) => slot.duration > MINIMUM_SEGMENT_DURATION
  )
  const speakerTagsInTranscript = sufficientlyLongTranscriptSpeakerSlots.map((transcriptSlot) => transcriptSlot.speaker.name!)
  const uniqueTags = [...new Set(speakerTagsInTranscript)]
  return uniqueTags.some((tag) => meeting.userSpeakerMapping![tag] === tag)
}

export function isDraftMeeting(meeting: { type: MeetingType; status: MeetingStatus }): boolean {
  return (meeting.type === MeetingType.VIDEO_UPLOAD || meeting.type === MeetingType.AUDIO_UPLOAD) && meeting.status === MeetingStatus.CREATED
}

export function hasEditAccess(meeting: { accessLevel: string }): boolean {
  return ['FULL_ACCESS', 'EDITOR'].includes(meeting.accessLevel)
}

/** Edit the minutes as tracked suggestions. A reviewer may do this but may not edit directly. */
export function hasSuggestAccess(meeting: { accessLevel: string }): boolean {
  return ['FULL_ACCESS', 'EDITOR', 'REVIEWER'].includes(meeting.accessLevel)
}

export function hasCommentAccess(meeting: { accessLevel: string }): boolean {
  return ['FULL_ACCESS', 'EDITOR', 'REVIEWER', 'COMMENTER'].includes(meeting.accessLevel)
}

/** Resolve a thread someone else started. Anyone with comment access may still resolve their own. */
export function canResolveAnyThread(meeting: { accessLevel: string }): boolean {
  return ['FULL_ACCESS', 'EDITOR', 'REVIEWER'].includes(meeting.accessLevel)
}

/** Mirrors the backend guard on the editor export endpoints. Every level clears it now that VIEWER is gone, so it stands as the guard's mirror rather than as a live restriction. */
export function canDownloadMinutes(meeting: { accessLevel: string; allowMinutesDownload: boolean }): boolean {
  return meeting.allowMinutesDownload && hasCommentAccess(meeting)
}

/** Whether a regeneration is in flight that rewrites only some agenda items, leaving the rest usable. */
export function isMinutesGenerationTargeted(meeting: { minutesGenerationTargetTemplateItemIds: number[] | null }): boolean {
  return meeting.minutesGenerationTargetTemplateItemIds !== null
}

/** Whether the outputs are stale relative to the meeting, independent of whether reprocessing them is possible right now. */
export function areOutputsStale(meeting: { isMinutingUpToDate: boolean; isOutlineUpToDate: boolean }): boolean {
  return !meeting.isMinutingUpToDate || !meeting.isOutlineUpToDate
}

/**
 * Whether to offer a reprocessing: the outputs are behind the meeting, and nothing is already redoing them.
 *
 * `isMinutesRegenerationInFlight` rather than `isMinutesGenerationOngoing` — a reprocessing that dispatches an
 * agenda-to-transcript mapping first spends that whole stage with its minutes job not yet started, and offering
 * the button there dispatches a second reprocessing on top of the one running.
 */
export function canReprocessOutputs(meeting: { isMinutingUpToDate: boolean; isOutlineUpToDate: boolean; isMinutesRegenerationInFlight: boolean }): boolean {
  return areOutputsStale(meeting) && !meeting.isMinutesRegenerationInFlight
}

/**
 * Whether the reprocess-outputs button is worth rendering at all: either there's something stale to redo, or a
 * regeneration is already under way and the button should stay visible - disabled, with a tooltip - to explain
 * why, rather than vanishing with no indication of what's happening. Unlike {@link canReprocessOutputs}, this
 * says nothing about whether clicking it would actually work right now.
 */
export function isReprocessOutputsButtonVisible(meeting: Meeting): boolean {
  return areOutputsStale(meeting) || meeting.isMinutesRegenerationInFlight
}

/**
 * Whether the meeting has settled enough to stop polling its details.
 *
 * `isPostProcessingComplete` on its own is not enough. It counts minutes generation as not required until
 * that job registers as started, so a meeting with no agenda flips the flag the moment the outline lands -
 * while the very same response still reports `GENERATING_MINUTES`. Stopping the poll there leaves that
 * progress bar on screen for good, since nothing will ever fetch the state that clears it.
 */
export function hasMeetingDetailsSettled(meeting: { isPostProcessingComplete: boolean; processingStatus: MeetingProcessingStatus | null }): boolean {
  return meeting.isPostProcessingComplete && (meeting.processingStatus ?? null) === null
}

type EditableMeeting = {
  accessLevel: string
  status: MeetingStatus
  isPostProcessingComplete: boolean
  minutesGenerationTargetTemplateItemIds: number[] | null
}

/** Whether the minutes are in a state that accepts changes at all, ignoring who is asking. */
function areMinutesModifiable(meeting: EditableMeeting): boolean {
  // A targeted regeneration leaves post-processing incomplete while it runs, but only the agenda items it covers
  // are off limits — the editor locks those individually and the rest of the minutes stays editable.
  return !(meeting.status === MeetingStatus.COMPLETED && !meeting.isPostProcessingComplete && !isMinutesGenerationTargeted(meeting))
}

export function canUserEdit(meeting: EditableMeeting): boolean {
  return hasEditAccess(meeting) && areMinutesModifiable(meeting)
}

/** A reviewer's counterpart to {@link canUserEdit}: the editor accepts input, but only as tracked changes. */
export function canUserSuggest(meeting: EditableMeeting): boolean {
  return hasSuggestAccess(meeting) && areMinutesModifiable(meeting)
}

export type SupportCreateMeetingData = {
  name: string
  link: string
  date: Date
  duration: number
  teamId: number
  userIds: number[]
}

export type LoadTestData = {
  name: string
  assistantCount: number
  link: string
  date: Date
  duration: number
  teamId: number
  userIds: number[]
}

type AdminMeetingListQueryFilters = {
  link?: string
  teamIds?: number[]
  displaySetToJoinOnly?: boolean
  userIds?: number[]
  statuses?: MeetingStatus[]
  startDate?: string
  endDate?: string
}
type AdminMeetingListQuery = AdminMeetingListQueryFilters & ListParameterQueryOptional

export type AdminCompletedMeetingOwner = {
  userId: number
  firstName: string
  lastName: string
  email: string
}

export type AdminCompletedMeeting = {
  meetingId: number
  team: { id: number; name: string; client: { id: number; name: string } }
  users: AdminCompletedMeetingOwner[]
  status: MeetingStatus
  type: MeetingType
  actualStartDate: string | null
  actualEndDate: string | null
  duration: number | null
  hardDeletedAt: string | null
  avgConfidenceScore: number | null
  avgRms: number | null
  avgHfRatio: number | null
}

type AdminCompletedMeetingQueryResult = {
  list: AdminCompletedMeeting[]
  total: number
}

type AdminCompletedMeetingListQueryFilters = {
  meetingId?: number
  teamIds?: number[]
  userIds?: number[]
}
type AdminCompletedMeetingListQuery = AdminCompletedMeetingListQueryFilters & ListParameterQueryOptional

export type AgendaTranscriptMappingJobStatus = 'NOT_STARTED' | 'ONGOING' | 'SUCCEEDED' | 'FAILED'

export type AgendaTranscriptMappingListItem = {
  meetingId: number
  teamId: number
  teamName: string
  clientName: string
  meetingDate: string
  durationSeconds: number
  jobRunId: number
  jobStatus: AgendaTranscriptMappingJobStatus
  agendaItemCount: number
  discussedCount: number
  notDiscussedCount: number
  lowConfidenceSegmentCount: number
  unmappedDiscussionCount: number
  transcriptionCoveragePercent: number | null
  isManuallyEdited: boolean
}

type AgendaTranscriptMappingListQueryResult = { list: AgendaTranscriptMappingListItem[]; total: number }

export type AgendaTranscriptMappingListQuery = {
  meetingId?: number
  teamIds?: number[]
  startDate?: string
  endDate?: string
  durationMinSeconds?: number
  durationMaxSeconds?: number
  jobStatus?: AgendaTranscriptMappingJobStatus
  hasLowConfidenceSegments?: boolean
  hasUnmappedDiscussions?: boolean
  discussedRatioMax?: number
  coverageMax?: number
  sortField?: 'meetingId' | 'meetingDate' | 'durationSeconds'
  sortDirection?: 'asc' | 'desc'
  page?: number
  pageSize?: number
}

export type MergeMeetingRecordingBlocker =
  | 'SAME_MEETING'
  | 'DIFFERENT_TEAMS'
  | 'TARGET_HAS_NO_RECORDING'
  | 'SOURCE_HAS_NO_RECORDING'
  | 'SOURCE_FILES_MISSING'
  | 'RECORDING_FORMAT_MISMATCH'
  | 'UPLOADED_RECORDING'
  | 'SEGMENTS_MANIFEST_MISMATCH'
  | 'NATIVE_TEAMS_RECORDING'
  | 'TARGET_IS_ONGOING'
  | 'SOURCE_IS_ONGOING'
  | 'TARGET_MARKED_FOR_DELETION'

export type MergeMeetingRecordingSummary = {
  id: number
  teamId: number
  teamName: string
  clientName: string
  date: string
  status: MeetingStatus
  type: MeetingType
  resourceType: string | null
  extension: string | null
  recordingSessionCount: number
  fileCount: number
}

export type MergeMeetingRecordingPreview = {
  target: MergeMeetingRecordingSummary
  source: MergeMeetingRecordingSummary
  blockers: MergeMeetingRecordingBlocker[]
  sessionsToAppend: number
  filesToCopy: number
}

export type MergeMeetingRecordingResult = {
  jobId: number
  appendedSessionIndices: number[]
  copiedFileCount: number
}

type MeetingDetailsErrorUser = {
  id: number
  firstName: string
  lastName: string
  email: string
}
type MeetingDetailsError = {
  hasAccess: boolean
  sameTeam: boolean
  meetingName: string
  users: MeetingDetailsErrorUser[]
}
type MeetingDetailsErrorData = {
  error: { message: MeetingDetailsError }
}

@Injectable({
  providedIn: 'root',
})
export class MeetingsService {
  mutation = injectMutation()
  httpClient = inject(HttpClient)
  query = injectQuery()
  infiniteQuery = injectInfiniteQuery()
  queryClient = injectQueryClient()
  toastService = inject(ToastService)
  downloadsService = inject(DownloadsService)
  demoProfilesService = inject(DemoProfilesService)
  loggerService = inject(LoggerService)
  metricsService = inject(MetricsService)

  private ongoingMeetingsFastPollEndsAt: Date | null = null

  searchGuestOrganizationOptionsQueryParams = {
    queryKey: queryKeys.search.guestOrganizations,
    queryFn: () => this.httpClient.get<GuestOrganization[]>('/api/search/options/guest-organizations'),
  }

  searchAttendeeOptionsQueryParams = {
    queryKey: queryKeys.search.attendees,
    queryFn: () => this.httpClient.get<Attendee[]>('/api/search/options/attendees'),
  }

  searchSpeakerOptionsQueryParam = {
    queryKey: queryKeys.search.speakers,
    queryFn: () => this.httpClient.get<Speaker[]>('/api/search/options/speakers'),
  }

  constructor() {
    this.listenForActiveDemoProfileUpdates()
  }

  forceProcessAssistantOutputsMutation() {
    return this.mutation({
      mutationFn: (meetingId: number) => this.httpClient.post<void>(`api/meetings/${meetingId}/process-assistant-outputs`, null),
      onSuccess: (_, meetingId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(meetingId) })
        this.toastService.show('MEETINGS.ADMIN.FORCE_PROCESS_ASSISTANT_OUTPUTS_SUCCESS')
      },
      onError: () => {
        this.toastService.show('MEETINGS.ADMIN.FORCE_PROCESS_ASSISTANT_OUTPUTS_FAILURE')
      },
    })
  }

  /**
   * Checks a pair of meetings before offering the merge: the API answers with both meetings and the
   * reasons, if any, their recordings cannot be combined.
   */
  previewRecordingMerge(targetMeetingId: number, sourceMeetingId: number): Observable<MergeMeetingRecordingPreview> {
    return this.httpClient.get<MergeMeetingRecordingPreview>(`/api/meetings/${targetMeetingId}/merge-recording-preview`, { params: { sourceMeetingId } })
  }

  mergeRecordingMutation() {
    return this.mutation({
      mutationFn: ({ targetMeetingId, sourceMeetingId }: { targetMeetingId: number; sourceMeetingId: number }) =>
        this.httpClient.post<MergeMeetingRecordingResult>(`/api/meetings/${targetMeetingId}/merge-recording`, { sourceMeetingId }),
      onSuccess: (_, { targetMeetingId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(targetMeetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(targetMeetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.base })
        this.toastService.show('MEETINGS.ADMIN.MERGE_RECORDING.SUCCESS')
      },
      onError: () => {
        this.toastService.show('MEETINGS.ADMIN.MERGE_RECORDING.FAILURE')
      },
    })
  }

  splitMeetingMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, recordingPositions }: { meetingId: number; recordingPositions: number[] }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/split`, { recordingPositions }),
      onSuccess: (_, { meetingId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(meetingId) })
        this.toastService.show('MEETINGS.ADMIN.SPLIT_MEETING.SUCCESS')
      },
      onError: () => {
        this.toastService.show('MEETINGS.ADMIN.SPLIT_MEETING.FAILURE')
      },
    })
  }

  reprocessOutputsMutation() {
    return this.mutation({
      mutationFn: ({ meetingId }: { meetingId: number }) => this.httpClient.post<void>(`api/meetings/${meetingId}/reprocess-outputs`, {}),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
        this.toastService.show('MEETINGS.REPROCESS.SUCCESS')
      },
      onError: () => {
        this.toastService.show('MEETINGS.REPROCESS.FAILURE')
      },
    })
  }

  createEditorDocumentMutation() {
    return this.mutation({
      mutationFn: ({ meetingId }: { meetingId: number }) => this.httpClient.post<void>(`api/meetings/${meetingId}/editor-document`, {}),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
      },
    })
  }

  reprocessTemplateItemsMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, templateItemIds }: { meetingId: number; templateItemIds: number[] }) =>
        this.httpClient.post<void>(`api/meetings/${meetingId}/template-items/reprocess`, { templateItemIds }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byMeetingId(params.meetingId) })
        this.toastService.show('MEETINGS.REPROCESS.SUCCESS')
      },
      onError: () => {
        this.toastService.show('MEETINGS.REPROCESS.FAILURE')
      },
    })
  }

  sendRecoveryAssistantMutation() {
    return this.mutation({
      mutationFn: (meetingId: number) => this.httpClient.post<SendRecoveryAssistantResult>(`api/meetings/${meetingId}/send-recovery-assistant`, null),
      onSuccess: (result, meetingId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.client.base })
        this.toastService.show(SEND_RECOVERY_ASSISTANT_TOASTS[result.outcome])
      },
      onError: () => {
        this.toastService.show('MEETINGS.ADMIN.SEND_RECOVERY_ASSISTANT.FAILURE')
      },
    })
  }

  sendDirectAssistantMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, startNow }: { meetingId: number; startNow: boolean }) =>
        this.httpClient.post<void>(`api/meetings/${meetingId}/send-direct-assistant`, { startNow }),
      onSuccess: (_, { meetingId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(meetingId) })
        this.toastService.show('MEETINGS.ADMIN.SEND_DIRECT_ASSISTANT.SUCCESS')
      },
      onError: () => {
        this.toastService.show('MEETINGS.ADMIN.SEND_DIRECT_ASSISTANT.FAILURE')
      },
    })
  }

  resendAssistantMutation() {
    return this.mutation({
      mutationFn: (meetingId: number) => this.httpClient.post<ResendAssistantResult>(`api/meetings/${meetingId}/resend-assistant`, null),
      onSuccess: (result, meetingId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.toastService.show(result.outcome === 'ALREADY_ON_ITS_WAY' ? 'MEETINGS.RESEND_ASSISTANT.ALREADY_ON_ITS_WAY' : 'MEETINGS.RESEND_ASSISTANT.SUCCESS')
      },
      onError: () => {
        this.toastService.show('MEETINGS.RESEND_ASSISTANT.FAILURE')
      },
    })
  }

  switchToBrowserAssistantMutation() {
    return this.mutation({
      mutationFn: (meetingId: number) =>
        this.httpClient.post<{ outcome: 'SWITCHED' | 'ALREADY_JOINED' | 'ALREADY_ON_ITS_WAY' }>(`api/meetings/${meetingId}/switch-to-browser-assistant`, null),
      onSuccess: (result, meetingId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.toastService.show(`MEETINGS.SWITCH_TO_BROWSER_ASSISTANT.${result.outcome}`)
      },
      onError: () => {
        this.toastService.show('MEETINGS.SWITCH_TO_BROWSER_ASSISTANT.FAILURE')
      },
    })
  }

  reprocessComponentMutation() {
    return this.mutation({
      mutationFn: ({
        meetingId,
        component,
        llmModel,
        llmEffort,
        stopAfterCompletion,
        minSpeakers,
        speakerCount,
        maxSpeakers,
        isDry,
        bypassNewAgendaTranscriptMappingJob,
        skipSilenceTrimming,
      }: {
        meetingId: number
        component: PostProcessor
        llmModel: LlmModel | null
        llmEffort: LlmEffort | null
        stopAfterCompletion: boolean
        minSpeakers: number | null
        speakerCount: number | null
        maxSpeakers: number | null
        isDry: boolean
        bypassNewAgendaTranscriptMappingJob: boolean
        skipSilenceTrimming: boolean
      }) => {
        const params = new URLSearchParams({ component })
        if (llmModel !== null) {
          params.append('llmModel', llmModel)
        }
        if (llmEffort !== null) {
          params.append('llmEffort', llmEffort)
        }
        if (stopAfterCompletion) {
          params.append('stopAfterCompletion', 'true')
        }
        if (minSpeakers !== null) {
          params.append('minSpeakers', String(minSpeakers))
        }
        if (speakerCount !== null) {
          params.append('speakerCount', String(speakerCount))
        }
        if (maxSpeakers !== null) {
          params.append('maxSpeakers', String(maxSpeakers))
        }
        if (isDry) {
          params.append('isDry', 'true')
        }
        if (bypassNewAgendaTranscriptMappingJob) {
          params.append('bypassNewAgendaTranscriptMappingJob', 'true')
        }
        if (skipSilenceTrimming) {
          params.append('skipSilenceTrimming', 'true')
        }
        return this.httpClient.post<void>(`api/meetings/${meetingId}/reprocess?${params.toString()}`, null)
      },
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
        this.toastService.show('MEETINGS.REPROCESS.SUCCESS')
      },
      onError: () => {
        this.toastService.show('MEETINGS.REPROCESS.FAILURE')
      },
    })
  }

  getUpcomingMeetings() {
    return this.infiniteQuery({
      queryKey: queryKeys.meetings.list.upcoming,
      queryFn: ({ pageParam }) => this.getUpcomingMeetingList(pageParam),
      initialPageParam: 0,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      refetchInterval: 10000,
    })
  }

  getOngoingMeetings() {
    return this.query({
      queryKey: queryKeys.meetings.list.ongoing,
      queryFn: () => this.getOngoingMeetingList(),
      refetchInterval: () => (this.ongoingMeetingsFastPollEndsAt !== null && new Date() < this.ongoingMeetingsFastPollEndsAt ? 1_000 : 10_000),
    }).result$
  }

  private pollOngoingMeetingsFast(): void {
    this.ongoingMeetingsFastPollEndsAt = new Date(Date.now() + ONGOING_MEETINGS_FAST_POLL_DURATION_MS)
    this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.list.ongoing })
  }

  getPastMeetings(meetingType: PastMeetingsType, showHiddenMeetings: boolean) {
    return this.infiniteQuery({
      queryKey: queryKeys.meetings.list.past.byType(meetingType),
      queryFn: ({ pageParam }) => this.getPastMeetingList(pageParam, meetingType, showHiddenMeetings),
      initialPageParam: 0,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      refetchInterval: 10000,
    })
  }
  getAdminMeetings(params: AdminMeetingListQuery) {
    return this.query({
      queryKey: queryKeys.meetings.base,
      queryFn: () => this.httpClient.get<AdminMeetingQueryResult>('/api/meetings/support', { params }),
    }).result$
  }

  getSupportCompletedMeetings(params: AdminCompletedMeetingListQuery) {
    return this.query({
      queryKey: [...queryKeys.meetings.support.completed, params],
      queryFn: () => this.httpClient.get<AdminCompletedMeetingQueryResult>('/api/meetings/support/completed-meetings', { params }),
    }).result$
  }

  getSupportAgendaTranscriptMappingList(params: AgendaTranscriptMappingListQuery) {
    return this.query({
      queryKey: [...queryKeys.meetings.support.agendaTranscriptMappings, params],
      queryFn: () => this.httpClient.get<AgendaTranscriptMappingListQueryResult>('/api/meetings/support/agenda-transcript-mappings', { params }),
    }).result$
  }

  getAgendaTranscriptMappingForMeeting(meetingId: number, jobId: number) {
    return this.query({
      queryKey: queryKeys.meetings.support.agendaTranscriptMapping(meetingId, jobId),
      queryFn: () =>
        this.httpClient.get<AgendaTranscriptMappingResponse | null>(`/api/meetings/${meetingId}/support/agenda-transcript-mapping`, { params: { jobId } }),
    }).result$
  }

  updateAgendaTranscriptMapping() {
    return this.mutation({
      mutationFn: ({ meetingId, jobId, items }: { meetingId: number; jobId: number; items: UpdateAgendaTranscriptMappingItem[] }) =>
        this.httpClient.put<void>(`/api/meetings/${meetingId}/support/agenda-transcript-mapping`, { items }, { params: { jobId } }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.agendaTranscriptMapping(params.meetingId, params.jobId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.agendaTranscriptMappings })
      },
      onError: () => {
        this.toastService.show('MEETINGS.ADMIN.A2T_EDIT.SAVE_FAILED')
      },
    })
  }

  getAdminMeetingsForUser(userId: number, params: AdminMeetingListQuery) {
    return this.query({
      queryKey: [...queryKeys.meetings.support.byUserId(userId), params],
      queryFn: () => this.httpClient.get<AdminMeetingQueryResult>('/api/meetings/support', { params: { ...params, userIds: [userId] } }),
    }).result$
  }

  getAdminMeetingsForTeam(teamId: number, params: AdminMeetingListQuery) {
    return this.query({
      queryKey: [...queryKeys.meetings.support.byTeamId(teamId), params],
      queryFn: () => this.httpClient.get<AdminMeetingQueryResult>('/api/meetings/support', { params: { ...params, teamIds: [teamId] } }),
    }).result$
  }

  getMeeting(id: number) {
    return this.query<Meeting, MeetingDetailsErrorData>(this.getMeetingQueryParams(id)).result$
  }

  getAdminViewToMeeting(id: number): Observable<QueryObserverResult<AdminMeetingDetails>> {
    return this.query({
      queryKey: queryKeys.meetings.support.byId(id),
      queryFn: () => this.httpClient.get<AdminMeetingDetails>(`/api/meetings/${id}/support`),
      refetchInterval: 30000,
    }).result$
  }

  getDebugDataFiles(id: number): Observable<QueryObserverResult<DebugDataFile[]>> {
    return this.query({
      queryKey: queryKeys.meetings.support.debugData(id),
      queryFn: () => this.httpClient.get<DebugDataFile[]>(`/api/meetings/${id}/support/debug-data`),
    }).result$
  }

  getEditorDebug(id: number): Observable<QueryObserverResult<EditorAdminDebug | null>> {
    return this.query({
      queryKey: queryKeys.meetings.support.editorDebug(id),
      queryFn: () => this.httpClient.get<EditorAdminDebug | null>(`/api/meetings/${id}/editor/support-debug`),
    }).result$
  }

  getHallucinationAudit(meetingId: number, jobId: number): Observable<QueryObserverResult<HallucinationAuditData>> {
    return this.query({
      queryKey: queryKeys.meetings.support.hallucinationAudit(meetingId, jobId),
      queryFn: () => this.httpClient.get<HallucinationAuditData>(`/api/meetings/${meetingId}/support/hallucination-audit/${jobId}`),
    }).result$
  }

  private getMeetingQueryParams(id: number) {
    let failureCount = 0

    return {
      queryKey: queryKeys.meetings.byId(id),
      queryFn: () => this.httpClient.get<Meeting>(`/api/meetings/${id}`),
      retry: 0,
      refetchInterval: (query: Query<Meeting, MeetingDetailsErrorData>) => {
        if (query.state.data?.isIngestingBoardPack ?? false) {
          return 10000
        }

        const meeting = query.state.data ?? null
        if (meeting !== null && hasMeetingDetailsSettled(meeting)) {
          return false
        }

        if (query.state.error?.error?.message?.sameTeam) {
          return false
        }

        const failed = query.state.fetchFailureReason !== null
        if (failed) {
          failureCount++
          if (failureCount === 3) {
            this.loggerService.error('The meeting details fetching failed three times. Stopping the refetch.')
            return false
          }
        }

        if (query.state.data?.isCheckingAgendaValidity ?? false) {
          return 1000
        }

        return 10000
      },
      staleTime: 10000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  }

  async prefetchMeeting(id: number) {
    await this.queryClient.prefetchQuery(this.getMeetingQueryParams(id))
  }

  getMeetingsOfUser(): Observable<QueryObserverResult<Meeting[]>> {
    const upcomingMeetings$ = this.httpClient
      .get<MeetingListResponse>('/api/meetings/upcoming', {
        params: { cursor: 0, limit: 1000 },
      })
      .pipe(map((response) => response.meetings))
    const pastMeetings$ = this.httpClient
      .get<MeetingListResponse>('/api/meetings/past', {
        params: {
          cursor: 0,
          limit: 1000,
          meetingType: 'ALL',
          showHiddenMeetings: true,
        },
      })
      .pipe(map((response) => response.meetings))
    return forkJoin([upcomingMeetings$, pastMeetings$]).pipe(
      map(([upcomingMeetings, pastMeetings]) => [...upcomingMeetings, ...pastMeetings]),
      mergeMap(
        (meetings) =>
          this.query<Meeting[]>({
            queryKey: queryKeys.meetings.base,
            queryFn: () => of(meetings),
          }).result$
      )
    )
  }

  uploadMeeting() {
    return this.mutation({
      mutationFn: (data: UploadMeetingData) => this.httpClient.post<Meeting>(`/api/meetings/upload`, data),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.list.past.base })
      },
    })
  }

  joinLiveMeeting() {
    return this.mutation({
      mutationFn: (data: AddMeetingData) => this.httpClient.post<Meeting>(`/api/meetings`, data),
      onError: (error: any) => {
        const errorMessages: { [key: string]: string } = {
          MEETING_WITH_LINK_ALREADY_EXISTS: 'MEETINGS.ADD_MEETING.MEETING_WITH_LINK_ALREADY_EXISTS',
          MEETING_LINK_INVALID: 'MEETINGS.ADD_MEETING.MEETING_LINK_INVALID',
          TEAMS_SHORT_LINK_NOT_SUPPORTED: 'MEETINGS.ADD_MEETING.TEAMS_SHORT_LINK_NOT_SUPPORTED',
        }
        const errorMessage = errorMessages[error?.error?.message] || 'MEETINGS.ADD_MEETING.FAILURE'
        this.toastService.show(errorMessage)
      },
    })
  }

  importResource() {
    return this.mutation({
      mutationFn: ({ meetingId, data }: { meetingId: number; data: ImportResourceData }) =>
        this.httpClient.post<MeetingResource>(`/api/meetings/${meetingId}/import-resource`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.toastService.show('MEETINGS.MEETING_INFO.DOCUMENT_UPLOAD.IMPORT_SUCCESS')
      },
    })
  }

  importAgenda() {
    return this.mutation({
      mutationFn: ({ meetingId, data }: { meetingId: number; data: ImportResourceData }) =>
        this.httpClient.post<MeetingResource>(`/api/meetings/${meetingId}/import-agenda`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.toastService.show('MEETINGS.MEETING_INFO.DOCUMENT_UPLOAD.IMPORT_SUCCESS')
      },
    })
  }

  removeResource() {
    return this.mutation({
      mutationFn: ({ meetingId, resourceType, index, extension }: { meetingId: number; resourceType: string; index: number; extension: string }) =>
        this.httpClient.delete<void>(`/api/meetings/${meetingId}/resources`, {
          body: { resourceType, index, extension },
        }),
      // Awaiting the refetch keeps the mutation pending until the removed resource has left the page, so its remove button cannot fire a second delete.
      onSuccess: (_, params) =>
        Promise.all([
          this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) }),
          this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byMeetingId(params.meetingId) }),
        ]),
      onError: () => {
        this.toastService.show('MEETINGS.MEETING_INFO.DOCUMENT_UPLOAD.REMOVE_FAILED')
      },
    })
  }

  removeAllResourcesOfType() {
    return this.mutation({
      mutationFn: ({ meetingId, resourceType }: { meetingId: number; resourceType: string }) =>
        this.httpClient.delete<void>(`/api/meetings/${meetingId}/resources/by-type`, {
          body: { resourceType },
        }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byMeetingId(params.meetingId) })
      },
      onError: () => {
        this.toastService.show('MEETINGS.MEETING_INFO.DOCUMENT_UPLOAD.REMOVE_FAILED')
      },
    })
  }

  removeResourceSupportMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, resourceType, index, extension }: { meetingId: number; resourceType: string; index: number; extension: string }) =>
        this.httpClient.delete<void>(`/api/meetings/${meetingId}/resources/support`, {
          body: { resourceType, index, extension },
        }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
      },
      onError: () => {
        this.toastService.show('MEETINGS.MEETING_INFO.DOCUMENT_UPLOAD.REMOVE_FAILED')
      },
    })
  }

  importRecording() {
    return this.mutation({
      mutationFn: ({ meetingId, uploadKey, fileExtension, fileName }: { meetingId: number; uploadKey: string; fileExtension: string; fileName: string }) =>
        this.httpClient.post<Meeting>(`/api/meetings/${meetingId}/import-recording`, { uploadKey, fileExtension, fileName }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
      },
      onError: () => {
        this.toastService.show('MEETING_SETUP.UPLOAD_RECORDING.UPLOAD_FAILED')
      },
    })
  }

  completeSetup() {
    return this.mutation({
      mutationFn: ({ meetingId }: { meetingId: number }) => this.httpClient.post<Meeting>(`/api/meetings/${meetingId}/complete-setup-for-uploaded-meeting`, {}),
      onSuccess: (_, params) => {
        this.queryClient.resetQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
      },
      onError: () => {
        this.toastService.show('MEETING_SETUP.COMPLETE_SETUP.FAILED')
      },
    })
  }

  patchMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, data }: { meetingId: number; data: Partial<Meeting> }) => this.httpClient.patch<void>(`/api/meetings/${meetingId}`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byMeetingId(params.meetingId) })
      },
    })
  }

  patchSupportMutation() {
    return this.mutation({
      mutationFn: ({
        meetingId,
        data,
      }: {
        meetingId: number
        data: Omit<
          Partial<Meeting>,
          | 'useNativeAssistant'
          | 'minutingAgents'
          | 'overrideTeamLevelMinutingAgents'
          | 'useVerificationDiscovery'
          | 'anonymiseMemberSpeakers'
          | 'useTranscriptRefinement'
          | 'allowRestrictedUsers'
        > & {
          useNativeAssistant?: boolean | null
          minutingAgents?: MinutingAgent[]
          overrideTeamLevelMinutingAgents?: boolean
          useVerificationDiscovery?: boolean | null
          useAgendaTranscriptMapping?: AgendaTranscriptMappingMode | null
          anonymiseMemberSpeakers?: boolean | null
          useTranscriptRefinement?: boolean | null
          allowRestrictedUsers?: boolean | null
        }
      }) => this.httpClient.patch<void>(`/api/meetings/${meetingId}/support`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byMeetingId(params.meetingId) })
      },
    })
  }

  updateAgendaTypeMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, data }: { meetingId: number; data: { agendaType: AgendaType } }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/agenda-type`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
      },
    })
  }

  updateAgendaTypeSupportMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, data }: { meetingId: number; data: { agendaType: AgendaType } }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/agenda-type/set-as-support`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
      },
    })
  }

  updateMediumMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, data }: { meetingId: number; data: { medium: MeetingMedium } }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/medium`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
      },
    })
  }

  updateMediumSupportMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, data }: { meetingId: number; data: { medium: MeetingMedium } }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/medium/set-as-support`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
      },
    })
  }

  updateMeetingProfileMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, data }: { meetingId: number; data: { meetingProfileId: number | null } }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/meeting-profile`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
      },
    })
  }

  updateMeetingProfileSupportMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, data }: { meetingId: number; data: { meetingProfileId: number | null } }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/meeting-profile/set-as-support`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
      },
    })
  }

  assignTemplateSupportMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, templateId }: { meetingId: number; templateId: number }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/assign-template`, { templateId }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byMeetingId(params.meetingId) })
        this.toastService.show('TEMPLATES.IMPORTED')
      },
      onError: () => {
        this.toastService.show('TEMPLATES.IMPORT_FAILED')
      },
    })
  }

  splitSpeakersMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, speakerTag }: { meetingId: number; speakerTag: string }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/split-speakers`, { speakerTag }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.toastService.show('MEETINGS.SPEAKERS.SPLIT_SUCCEEDED')
      },
      onError: () => {
        this.toastService.show('MEETINGS.SPEAKERS.SPLIT_FAILED')
      },
    })
  }

  patchSpeakerNameMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, speakerTag, speakerName }: { meetingId: number; speakerTag: string; speakerName: string }) =>
        this.httpClient.patch<void>(`/api/meetings/${meetingId}/speaker-mapping`, { speakerTag, speakerName }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
      },
    })
  }

  pauseRecording(meetingId: number) {
    return this.httpClient.post<void>(`/api/meetings/${meetingId}/pause-recording`, {}).subscribe({
      next: () => {
        this.pollOngoingMeetingsFast()
        this.toastService.show('MEETINGS.ONGOING_MEETINGS.RECORDING_PAUSE.SUCCESS')
      },
      error: () => {
        this.toastService.show('MEETINGS.ONGOING_MEETINGS.RECORDING_PAUSE.FAILURE')
      },
    })
  }

  resumeRecording(meetingId: number) {
    return this.httpClient.post<void>(`/api/meetings/${meetingId}/resume-recording`, {}).subscribe({
      next: () => {
        this.pollOngoingMeetingsFast()
        this.toastService.show('MEETINGS.ONGOING_MEETINGS.RECORDING_RESUME.SUCCESS')
      },
      error: () => {
        this.toastService.show('MEETINGS.ONGOING_MEETINGS.RECORDING_RESUME.FAILURE')
      },
    })
  }

  hangUpMeeting(meetingId: number) {
    this.httpClient.post<void>(`/api/meetings/${meetingId}/hang-up`, {}).subscribe({
      next: () => {
        this.toastService.show('MEETINGS.HANG_UP.SUCCESS')
      },
      error: () => {
        this.toastService.show('MEETINGS.HANG_UP.FAILURE')
      },
    })
  }

  hangUpAsSupportMutation() {
    return this.mutation({
      mutationFn: (meetingId: number) => this.httpClient.post<void>(`/api/meetings/${meetingId}/hang-up-as-support`, {}),
      onSuccess: (_, meetingId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(meetingId) })
        this.toastService.show('MEETINGS.HANG_UP.SUCCESS')
      },
      onError: () => {
        this.toastService.show('MEETINGS.HANG_UP.FAILURE')
      },
    })
  }

  solveCaptchaMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, code }: { meetingId: number; code: string }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/support/solve-captcha`, { code }),
      onSuccess: (_, { meetingId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.debugData(meetingId) })
        this.toastService.show('MEETINGS.ADMIN.SOLVE_CAPTCHA.SUCCESS')
      },
      onError: () => {
        this.toastService.show('MEETINGS.ADMIN.SOLVE_CAPTCHA.FAILURE')
      },
    })
  }

  hangUpMeetingAnonymously(meetingId: number, accessKey: string): Observable<boolean> {
    return this.httpClient.post<void>(`/api/assistant/${meetingId}/hang-up`, {}, { headers: { 'X-Meeting-Access-Key': accessKey } }).pipe(
      map(() => {
        this.toastService.show('MEETINGS.HANG_UP.SUCCESS')
        return true
      }),
      catchError(() => {
        this.toastService.show('MEETINGS.HANG_UP.FAILURE')
        return of(false)
      })
    )
  }

  previewExtendMeetingAnonymously(meetingId: number, accessKey: string): Observable<ExtendPreview> {
    return this.httpClient
      .get<ExtendPreview>(`/api/assistant/${meetingId}/extend`, { headers: { 'X-Meeting-Access-Key': accessKey } })
      .pipe(catchError(() => of({ status: 'ERROR' as const, leaveDate: null })))
  }

  extendMeetingAnonymously(meetingId: number, accessKey: string): Observable<ExtendResult> {
    return this.httpClient
      .post<ExtendResult>(`/api/assistant/${meetingId}/extend`, {}, { headers: { 'X-Meeting-Access-Key': accessKey } })
      .pipe(catchError(() => of({ status: 'ERROR' as const, leaveDate: null })))
  }

  switchToNextSubMeetingMutation() {
    return this.mutation({
      mutationFn: (meetingId: number) => this.httpClient.post<void>(`/api/meetings/${meetingId}/save-split-offset`, {}),
      onSuccess: (_, meetingId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.list.ongoing })
        this.toastService.show('MEETINGS.ONGOING_MEETINGS.PROCEED_TO_NEXT_SUB_MEETING.SUCCESS')
      },
      onError: () => {
        this.toastService.show('MEETINGS.ONGOING_MEETINGS.PROCEED_TO_NEXT_SUB_MEETING.FAILURE')
      },
    })
  }

  setEndDateMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, endDate }: { meetingId: number; endDate: Date }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/set-end-date`, { endDate: endDate.toISOString() }),
      onSuccess: (_, { meetingId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(meetingId) })
      },
    })
  }

  extendMeetingMutation() {
    return this.mutation({
      mutationFn: (meetingId: number) =>
        firstValueFrom(
          this.httpClient.post<ExtendResult>(`/api/meetings/${meetingId}/extend`, {}).pipe(catchError(() => of({ status: 'ERROR' as const, leaveDate: null })))
        ),
      onSuccess: (_, meetingId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.list.ongoing })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
      },
    })
  }

  setDeletionDatesMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, softDeletionDate, hardDeletionDate }: { meetingId: number; softDeletionDate: Date; hardDeletionDate: Date }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/set-deletion-dates`, {
          softDeletionDate: softDeletionDate.toISOString(),
          hardDeletionDate: hardDeletionDate.toISOString(),
        }),
      onSuccess: (_, { meetingId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(meetingId) })
        this.toastService.show('MEETINGS.ADJUST_RETENTION.SUCCESS')
      },
      onError: () => {
        this.toastService.show('MEETINGS.ADJUST_RETENTION.FAILURE')
      },
    })
  }

  requestMeetingAccess(meetingId: number) {
    return this.httpClient.post<void>(`/api/meetings/${meetingId}/request-access`, {}).subscribe({
      next: () => {
        this.toastService.show('MEETINGS.ACCESS_REQUEST.SUCCESS')
      },
      error: () => {
        this.toastService.show('MEETINGS.ACCESS_REQUEST.FAILURE')
      },
    })
  }

  setIsSetToJoinMutation() {
    return this.mutation({
      mutationFn: (meetingId: number) => this.httpClient.post<void>(`/api/meetings/${meetingId}/set-to-join`, {}),
      onSuccess: (_, meetingId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(meetingId) })
      },
    })
  }

  cancelSessionCloudRunExecutionMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, sessionIndex }: { meetingId: number; sessionIndex: number }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/sessions/${sessionIndex}/cancel-cloud-run-execution`, {}),
      onSuccess: (_, { meetingId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(meetingId) })
        this.toastService.show('MEETINGS.ADMIN.CANCEL_CLOUD_RUN_EXECUTION.SUCCESS')
      },
      onError: () => {
        this.toastService.show('MEETINGS.ADMIN.CANCEL_CLOUD_RUN_EXECUTION.FAILURE')
      },
    })
  }

  setSoftDeletionMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, markedToBeDeleted }: { meetingId: number; markedToBeDeleted: boolean }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/set-soft-deletion`, { markedToBeDeleted }),
      onSuccess: (_, { meetingId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.list.base })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(meetingId) })
      },
    })
  }

  setMeetingStatusMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, status }: { meetingId: number; status: MeetingStatus }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/set-status`, { status }),
      onSuccess: (_, { meetingId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(meetingId) })
      },
    })
  }

  setMeetingSessionStatusMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, sessionIndex, status }: { meetingId: number; sessionIndex: number; status: MeetingSessionStatus }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/sessions/${sessionIndex}/set-status`, { status }),
      onSuccess: (_, { meetingId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(meetingId) })
      },
    })
  }

  approveAccessRequest() {
    return this.mutation({
      mutationFn: (meetingId: number) => this.httpClient.post<void>(`/api/meetings/${meetingId}/approve-access`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.accessRequests.base })
      },
    })
  }

  revokeAccessRequest() {
    return this.mutation({
      mutationFn: (meetingId: number) => this.httpClient.post<void>(`/api/meetings/${meetingId}/revoke-access`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.accessRequests.base })
      },
    })
  }

  getMeetingAccessRequest(meetingId: number) {
    return this.query({
      queryKey: queryKeys.meetings.accessRequests.byId(meetingId),
      queryFn: () =>
        this.httpClient.get<{
          meetingId: number
          accessProvided: boolean
        }>(`/api/meetings/${meetingId}/check-access`),
    }).result$
  }

  getMeetingAccessRequests() {
    return this.query({
      queryKey: queryKeys.meetings.accessRequests.base,
      queryFn: () => this.httpClient.get<MeetingAccessRequest[]>('/api/meetings/access-requests'),
    }).result$
  }

  exportTranscript(meeting: Meeting, format: 'pdf' | 'docx'): Observable<void> {
    const fileName = generateFileName(meeting, 'transcript', format)
    return this.httpClient
      .get(`/api/meetings/${meeting.id}/export/${format}?outlineSelected=false&transcriptSelected=true`, {
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, format)
          this.toastService.show('MEETINGS.TRANSCRIPT_EXPORT_COMPLETED')
        }),
        map(() => void 0)
      )
  }

  exportMinutes(
    meeting: Meeting,
    format: 'pdf' | 'docx',
    entity: { id: number; name: string } | null = null,
    revision: { id: number; createdAt: string } | null = null
  ): Observable<void> {
    const baseFileName = generateFileName(meeting, 'minutes', format)
    const entityFileName = entity === null ? baseFileName : qualifyFileName(baseFileName, entity.name)
    // Windows rejects a colon in a file name, so the time is written with a dot.
    const fileName =
      revision === null ? entityFileName : qualifyFileName(entityFileName, `version ${formatDate(new Date(revision.createdAt), 'dd-MM-yyyy HH.mm')}`)
    return this.httpClient
      .get(`/api/meetings/${meeting.id}/export-editor-to-${format}`, {
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
        params: {
          ...(entity === null ? {} : { meetingEntityId: entity.id }),
          ...(revision === null ? {} : { revisionId: revision.id }),
        },
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, format)
          this.toastService.show('MEETINGS.MINUTES_EXPORT_COMPLETED')
        }),
        map(() => void 0)
      )
  }

  exportOutline(meeting: Meeting, format: 'pdf' | 'docx'): Observable<void> {
    const fileName = generateFileName(meeting, 'outline', format)
    return this.httpClient
      .get(`/api/meetings/${meeting.id}/export/${format}?outlineSelected=true&transcriptSelected=false`, {
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, format)
          this.toastService.show('MEETINGS.OUTLINE_EXPORT_COMPLETED')
        }),
        map(() => void 0)
      )
  }

  exportActionItems(meeting: Meeting, format: 'csv'): Observable<void> {
    const fileName = generateFileName(meeting, 'action-items', format)
    return this.httpClient
      .get(`/api/meetings/${meeting.id}/export/action-items`, {
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, format)
          this.toastService.show('MEETINGS.ACTION_ITEMS_EXPORT_COMPLETED')
        }),
        map(() => void 0)
      )
  }

  getSearchGuestOrganizationOptions() {
    return this.query(this.searchGuestOrganizationOptionsQueryParams).result$
  }

  async prefetchSearchFilterOptions() {
    await this.queryClient.prefetchQuery(this.searchGuestOrganizationOptionsQueryParams)
    await this.queryClient.prefetchQuery(this.searchAttendeeOptionsQueryParams)
    // await this.queryClient.prefetchQuery(this.searchSpeakerOptionsQueryParam)
  }

  getSearchAttendeeOptions() {
    return this.query(this.searchAttendeeOptionsQueryParams).result$
  }

  getSearchSpeakerOptions() {
    return this.query(this.searchSpeakerOptionsQueryParam).result$
  }

  private getUpcomingMeetingList(cursor: number): Observable<MeetingListResponse> {
    const params = this.addDemoProfileId({
      cursor,
    })
    return this.httpClient.get<MeetingListResponse>('/api/meetings/upcoming', { params })
  }

  private getOngoingMeetingList(): Observable<MeetingWithLatestSession[]> {
    return this.httpClient.get<MeetingWithLatestSession[]>('/api/meetings/ongoing')
  }

  private getPastMeetingList(cursor: number, meetingType: PastMeetingsType, showHiddenMeetings: boolean): Observable<MeetingListResponse> {
    const params = this.addDemoProfileId({
      cursor,
      meetingType,
      showHiddenMeetings,
    })
    return this.httpClient.get<MeetingListResponse>('/api/meetings/past', { params })
  }

  search(filters: MeetingFilters): Observable<QueryObserverResult<SearchResponse>> {
    return this.query<SearchResponse>({
      queryKey: queryKeys.search.results.byFilters(filters),
      queryFn: () => this.searchMeetings(filters),
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }).result$
  }

  private searchMeetings(filters: MeetingFilters): Observable<SearchResponse> {
    const data = this.addDemoProfileId({
      teamIds: filters.guestOrganizationId !== null ? [filters.guestOrganizationId] : [],
      attendees: filters.attendeeId !== null ? [filters.attendeeId] : [],
      startDate: filters.startDate,
      endDate: filters.endDate,
      keyword: filters.query,
      speakers: filters.speakerIds,
      showHiddenMeetings: false, // TODO
      showOnlyPastMeetings: filters.showOnlyPastMeetings,
    })
    return this.httpClient.post<SearchResponse>('/api/search/meetings', data)
  }

  updateMeetingAccessLevel(meetingId: number) {
    return this.mutation({
      mutationFn: ({ sharedUserId, level }: { sharedUserId: number; level: MeetingAccessLevel }) =>
        this.httpClient.post<Meeting>(`/api/meetings/${meetingId}/update-share-level-for-shared-user`, { sharedUserId, level }),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
      },
    })
  }

  shareToUser(meetingId: number) {
    return this.mutation({
      mutationFn: ({ sharedUserId, level, notifyByEmail }: { sharedUserId: number; level: MeetingAccessLevel; notifyByEmail: boolean }) =>
        this.httpClient.post<MeetingShare>(`/api/meetings/${meetingId}/share-to-user`, { sharedUserId, level, notifyByEmail }),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
      },
    })
  }

  removeShareMutation(meetingId: number) {
    return this.mutation({
      mutationFn: (sharedUserId: number) => this.httpClient.delete<void>(`/api/meetings/${meetingId}/shares/${sharedUserId}`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
      },
    })
  }

  assignSentencesToSpeakerMutation() {
    return this.mutation({
      mutationFn: ({
        meetingId,
        startSentenceId,
        endSentenceId,
        speakerTag,
      }: {
        meetingId: number
        startSentenceId: string
        endSentenceId: string
        speakerTag: string
      }) => this.httpClient.post<void>(`/api/meetings/${meetingId}/assign-sentences-to-speaker`, { startSentenceId, endSentenceId, speakerTag }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) }, {})
      },
    })
  }

  addSpeakerMutation() {
    return this.mutation({
      mutationFn: ({
        meetingId,
        speakerName,
        startSentenceId,
        endSentenceId,
      }: {
        meetingId: number
        speakerName: string
        startSentenceId: string
        endSentenceId: string
      }) => this.httpClient.post<void>(`/api/meetings/${meetingId}/add-speaker`, { speakerName, startSentenceId, endSentenceId }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) }, {})
      },
    })
  }

  resetSpeakersMutation() {
    return this.mutation({
      mutationFn: ({ meetingId }: { meetingId: number }) => this.httpClient.post<void>(`/api/meetings/${meetingId}/reset-speakers`, {}),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) }, {})
      },
    })
  }

  toggleExcludeMeetingMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, isSetToJoin }: { meetingId: number; isSetToJoin: boolean }) =>
        this.httpClient.put(`/api/meetings/${meetingId}/user-data`, { isSetToJoin }),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.list.base })
      },
    })
  }

  deleteMeetingMutation() {
    return this.mutation({
      mutationFn: (meetingId: number) => this.httpClient.delete<void>(`/api/meetings/${meetingId}`),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.list.base })
      },
    })
  }

  toggleNotificationSetting() {
    return this.mutation({
      mutationFn: ({ meetingId, notifyWhenPostProcessingCompletes }: { meetingId: number; notifyWhenPostProcessingCompletes: boolean }) =>
        this.httpClient.put(`/api/meetings/${meetingId}/user-data`, { notifyWhenPostProcessingCompletes }),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.list.base })
      },
    })
  }

  forceDisableJoiningMutation() {
    return this.mutation({
      mutationFn: (meetingId: number) => this.httpClient.post<void>(`/api/meetings/${meetingId}/force-disable-joining`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.list.upcoming })
      },
    })
  }

  toggleMeetingOwnerJoinMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, userId, isSetToJoin }: { meetingId: number; userId: number; isSetToJoin: boolean }) =>
        this.httpClient.put<void>(`/api/meetings/${meetingId}/user-data/toggle-join`, { userId, isSetToJoin }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
      },
    })
  }

  updateMeetingOwnerSupportMutation() {
    return this.mutation({
      mutationFn: ({
        meetingId,
        userId,
        hasWeekendDeletionReminderEmailBeenSent,
        isPrimaryCalendar,
      }: {
        meetingId: number
        userId: number
        hasWeekendDeletionReminderEmailBeenSent?: boolean
        isPrimaryCalendar?: boolean
      }) => this.httpClient.put<void>(`/api/meetings/${meetingId}/user-data/support`, { userId, hasWeekendDeletionReminderEmailBeenSent, isPrimaryCalendar }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
      },
    })
  }

  deleteMeetingOwnerAsSupportMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, userId }: { meetingId: number; userId: number }) =>
        this.httpClient.delete<void>(`/api/meetings/${meetingId}/meeting-owners/support/${userId}`),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.client.base })
      },
    })
  }

  // One route for either kind of access, so the dialog never has to work out which of the two tables
  // a person is currently in before changing their role.
  setMeetingAccessMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, userId, role }: { meetingId: number; userId: number; role: MeetingAccessRole }) =>
        this.httpClient.put<void>(`/api/meetings/${meetingId}/access/${userId}`, { role }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.client.base })
      },
    })
  }

  removeMeetingAccessMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, userId }: { meetingId: number; userId: number }) => this.httpClient.delete<void>(`/api/meetings/${meetingId}/access/${userId}`),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.client.base })
      },
    })
  }

  addMeetingOwnerAsSupportMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, userId }: { meetingId: number; userId: number }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/meeting-owners/support`, { userId }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.client.base })
      },
    })
  }

  addMeetingOwnerMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, userId }: { meetingId: number; userId: number }) =>
        this.httpClient.put<void>(`/api/meetings/${meetingId}/meeting-owners/${userId}`, {}),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
      },
    })
  }

  removeMeetingOwnerMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, userId }: { meetingId: number; userId: number }) =>
        this.httpClient.delete<void>(`/api/meetings/${meetingId}/meeting-owners/${userId}`),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
      },
    })
  }

  getClientMeetings(
    nameQuery: string | null,
    includeAllStatuses: boolean,
    dateRange: DateRange,
    sortField: string | null,
    sortDirection: string | null,
    page: number,
    pageSize: number
  ) {
    const params = {
      ...(nameQuery ? { nameQuery } : {}),
      ...(includeAllStatuses ? { includeAllStatuses: true } : {}),
      ...(dateRange.startDate !== null ? { startDate: dateRange.startDate.toISOString() } : {}),
      ...(dateRange.endDate !== null ? { endDate: dateRange.endDate.toISOString() } : {}),
      ...(sortField ? { sortField } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      page,
      pageSize,
    }
    return this.query({
      queryKey: queryKeys.meetings.client.list(params),
      queryFn: () => this.httpClient.get<ClientMeetingQueryResult>('/api/meetings/client', { params }),
    }).result$
  }

  getRetentionExtensions(meetingId: number) {
    return this.query({
      queryKey: queryKeys.meetings.client.retentionExtensions(meetingId),
      queryFn: () => this.httpClient.get<MeetingRetentionExtension[]>(`/api/meetings/${meetingId}/retention-extensions`),
    }).result$
  }

  extendRetentionPeriodMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, deletionDate, softDeletionDate }: { meetingId: number; deletionDate: Date; softDeletionDate: Date }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/extend-retention-period`, {
          deletionDate: deletionDate.toISOString(),
          softDeletionDate: softDeletionDate.toISOString(),
        }),
      onSuccess: (_, { meetingId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.client.base })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.toastService.show('MEETINGS.EXTEND_RETENTION.SUCCESS')
      },
      onError: () => {
        this.toastService.show('MEETINGS.EXTEND_RETENTION.FAILURE')
      },
    })
  }

  restoreMeetingMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, dates }: { meetingId: number; dates: { softDeletionDate: Date; deletionDate: Date } | null }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/restore`, {
          ...(dates !== null ? { softDeletionDate: dates.softDeletionDate.toISOString(), deletionDate: dates.deletionDate.toISOString() } : {}),
        }),
      onSuccess: (_, { meetingId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.client.base })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.list.base })
        this.toastService.show('MEETINGS.ADMIN.SOFT_DELETION.UNMARK_SUCCESS')
      },
    })
  }

  getRetentionExtensionRequestOptions(meetingId: number) {
    return this.query({
      queryKey: queryKeys.meetings.retentionExtensionRequests.options(meetingId),
      queryFn: () => this.httpClient.get<RetentionExtensionRequestOptions>(`/api/meetings/${meetingId}/retention-extension-requests/options`),
    }).result$
  }

  requestRetentionExtensionMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, extensionInDays }: { meetingId: number; extensionInDays: number }) =>
        this.httpClient.post<PendingRetentionExtensionRequest>(`/api/meetings/${meetingId}/retention-extension-requests`, { extensionInDays }),
      onSuccess: (_, { meetingId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.retentionExtensionRequests.options(meetingId) })
        this.toastService.show('MEETINGS.RETENTION_EXTENSION_REQUEST.SUCCESS')
      },
      onError: () => {
        this.toastService.show('MEETINGS.RETENTION_EXTENSION_REQUEST.FAILURE')
      },
    })
  }

  getRetentionExtensionRequest(meetingId: number, requestId: number) {
    return this.query({
      queryKey: queryKeys.meetings.retentionExtensionRequests.byId(meetingId, requestId),
      queryFn: () => this.httpClient.get<RetentionExtensionRequestReview>(`/api/meetings/${meetingId}/retention-extension-requests/${requestId}`),
    }).result$
  }

  decideRetentionExtensionRequestMutation() {
    return this.mutation({
      mutationFn: ({ meetingId, requestId, decision }: { meetingId: number; requestId: number; decision: 'approve' | 'decline' }) =>
        this.httpClient.post<void>(`/api/meetings/${meetingId}/retention-extension-requests/${requestId}/${decision}`, {}),
      onSuccess: (_, { meetingId, decision }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.retentionExtensionRequests.base })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.client.base })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
        this.toastService.show(
          decision === 'approve' ? 'MEETINGS.RETENTION_EXTENSION_REQUEST.APPROVE_SUCCESS' : 'MEETINGS.RETENTION_EXTENSION_REQUEST.DECLINE_SUCCESS'
        )
      },
      onError: () => {
        this.toastService.show('MEETINGS.RETENTION_EXTENSION_REQUEST.DECISION_FAILURE')
      },
    })
  }

  private addDemoProfileId(params: any) {
    const demoProfileId = this.demoProfilesService.activeDemoProfileId$.value
    if (demoProfileId === null) {
      return params
    }
    return { ...params, demoProfileId }
  }

  private listenForActiveDemoProfileUpdates() {
    this.demoProfilesService.activeDemoProfileId$.subscribe(() => {
      this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.list.base })
    })
  }

  addAttendeeMutation() {
    return this.mutation({
      mutationFn: (params: { meetingId: number; data: Omit<AdminAgendaAttendee, 'id'> }) =>
        this.httpClient.post<AdminAgendaAttendee>(`/api/meetings/${params.meetingId}/attendees`, params.data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
      },
    })
  }

  updateAttendeeMutation() {
    return this.mutation({
      mutationFn: (params: { meetingId: number; data: AdminAgendaAttendee }) =>
        this.httpClient.put<AdminAgendaAttendee>(`/api/meetings/${params.meetingId}/attendees/${params.data.id}`, params.data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
      },
    })
  }

  deleteAttendeeMutation() {
    return this.mutation({
      mutationFn: (params: { meetingId: number; attendeeId: number }) =>
        this.httpClient.delete<void>(`/api/meetings/${params.meetingId}/attendees/${params.attendeeId}`),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
      },
    })
  }

  updateMetadataMutation() {
    return this.mutation({
      mutationFn: (params: { meetingId: number; data: Partial<AdminAgendaMetadata> }) =>
        this.httpClient.patch<AdminAgendaMetadata>(`/api/meetings/${params.meetingId}/metadata`, params.data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
      },
    })
  }

  joinNowMutation() {
    return this.mutation({
      mutationFn: (meetingId: number) => this.httpClient.post<void>(`/api/meetings/${meetingId}/join-now`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.list.upcoming })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.list.ongoing })
      },
    })
  }

  createMeetingAsSupportMutation() {
    return this.mutation({
      mutationFn: (data: SupportCreateMeetingData) => this.httpClient.post<Meeting>(`/api/meetings/support`, data),
    })
  }

  createLoadTestMutation() {
    return this.mutation({
      mutationFn: (data: LoadTestData) =>
        this.httpClient.post<{ success: boolean; meetingCount: number; meetings: { id: number; name: string }[] }>(`/api/meetings/load-test`, data),
    })
  }

  approveMissingAgendaMutation() {
    return this.mutation({
      mutationFn: (meetingId: number) => this.httpClient.post<Meeting>(`/api/meetings/${meetingId}/approve-missing-agenda`, {}),
      onSuccess: (_, meetingId: number) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(meetingId) })
      },
    })
  }

  getMeetingTimeline(meetingId: number) {
    return this.query<MeetingTimelineResponse>({
      queryKey: [...queryKeys.meetings.byId(meetingId), 'timeline'],
      queryFn: () => this.httpClient.get<MeetingTimelineResponse>(`/api/meetings/${meetingId}/timeline`),
    }).result$
  }

  ingestBoardPackMutation() {
    return this.mutation({
      mutationFn: (data: { meetingId: number; uploadKey: string; fileName: string }) => {
        return this.httpClient.post<{ jobId: number }>(`/api/meetings/${data.meetingId}/ingest-board-pack`, {
          uploadKey: data.uploadKey,
          fileName: data.fileName,
        })
      },
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
        this.toastService.show('MEETINGS.ADMIN.BOARD_PACK.INGEST_SUCCESS')
      },
    })
  }

  extractParticipantsMutation() {
    return this.mutation({
      mutationFn: (data: { meetingId: number; uploadKey: string; fileName: string; fileExtension: string }) => {
        return this.httpClient.post<{ jobId: number }>(`/api/meetings/${data.meetingId}/extract-participants`, {
          uploadKey: data.uploadKey,
          fileName: data.fileName,
          fileExtension: data.fileExtension,
        })
      },
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
      },
      onError: () => {
        this.toastService.show('MEETING_SETUP.EXTRACT_PARTICIPANTS.FAILED')
      },
    })
  }

  replaceAttendeesSupportMutation() {
    return this.mutation({
      mutationFn: (params: { meetingId: number; attendees: Omit<AdminAgendaAttendee, 'id'>[] }) =>
        this.httpClient.put<AdminAgendaAttendee[]>(`/api/meetings/${params.meetingId}/attendees-support`, { attendees: params.attendees }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
      },
    })
  }

  extractParticipantsSupportMutation() {
    return this.mutation({
      mutationFn: (data: { meetingId: number; uploadKey: string; fileName: string; fileExtension: string }) => {
        return this.httpClient.post<{ jobId: number }>(`/api/meetings/${data.meetingId}/extract-participants-support`, {
          uploadKey: data.uploadKey,
          fileName: data.fileName,
          fileExtension: data.fileExtension,
        })
      },
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.byId(params.meetingId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetings.support.byId(params.meetingId) })
      },
      onError: () => {
        this.toastService.show('MEETING_SETUP.EXTRACT_PARTICIPANTS.FAILED')
      },
    })
  }
}
